<template>
  <div class="bim-projects">
    <div class="bim-projects__header">
      <div class="header-controls">
        <label class="header-label">Тип проекта:</label>
        <select
          v-model="selectedProjectTypeId"
          @change="onProjectTypeChange"
          class="simple-select"
          :disabled="isLoadingProjectTypes"
        >
          <option :value="null">Все типы проектов</option>
          <option
            v-for="projectType in projectTypesOptions"
            :key="projectType.project_type_id"
            :value="projectType.project_type_id"
          >
            {{ projectType.project_type_name }}
          </option>
        </select>
      </div>
    </div>
    <div class="bim-projects__content">
      <!-- Колонка со списком проектов -->
      <div class="bim-projects__column projects-column">
        <div class="column-header">
          <q-icon name="folder_open" size="14px" />
          <span class="column-title">Проекты</span>
          <q-badge
            v-if="projects.length"
            :label="filteredProjects.length + '/' + projects.length"
            color="grey-4"
            text-color="grey-8"
          />
          <q-btn
            icon="add"
            flat
            dense
            round
            size="xs"
            color="primary"
            @click="openProjectDialog()"
          >
            <q-tooltip>Добавить проект</q-tooltip>
          </q-btn>
        </div>
        <div v-if="projects.length" class="search-container">
          <q-input
            v-model="projectSearchQuery"
            dense
            outlined
            placeholder="Поиск..."
            clearable
            class="search-input"
          >
            <template v-slot:prepend>
              <q-icon name="search" size="16px" />
            </template>
          </q-input>
        </div>
        <div
          v-if="!isLoadingProjects && projectTree.length > 0"
          class="column-list tree-list"
        >
          <template v-for="resident in projectTree" :key="resident.key">
            <div
              class="tree-node tree-node--resident"
              @click="toggleTreeKey(resident.key)"
            >
              <q-icon
                :name="expandedTreeKeys[resident.key] ? 'expand_more' : 'chevron_right'"
                size="18px"
                class="tree-node__icon"
              />
              <span class="tree-node__label">{{ resident.label }}</span>
            </div>
            <template v-if="expandedTreeKeys[resident.key]">
              <template v-for="type in resident.children" :key="type.key">
                <div
                  class="tree-node tree-node--type"
                  @click="toggleTreeKey(type.key)"
                >
                  <q-icon
                    :name="expandedTreeKeys[type.key] ? 'expand_more' : 'chevron_right'"
                    size="16px"
                    class="tree-node__icon"
                  />
                  <span class="tree-node__label">{{ type.label }}</span>
                </div>
                <template v-if="expandedTreeKeys[type.key]">
                  <template v-for="block in type.children" :key="block.key">
                    <div
                      class="tree-node tree-node--block"
                      @click="toggleTreeKey(block.key)"
                    >
                      <q-icon
                        :name="expandedTreeKeys[block.key] ? 'expand_more' : 'chevron_right'"
                        size="14px"
                        class="tree-node__icon"
                      />
                      <span class="tree-node__label">{{ block.label }}</span>
                    </div>
                    <template v-if="expandedTreeKeys[block.key]">
                      <q-item
                        v-for="project in block.projects"
                        :key="project.bim_project_id"
                        clickable
                        v-ripple
                        :active="selectedProject?.bim_project_id === project.bim_project_id"
                        @click="selectProject(project)"
                        class="list-item tree-leaf"
                        dense
                      >
                        <q-item-section>
                          <q-item-label class="item-label">
                            {{ project.project_name }}
                            <span class="item-label-id">(ID: {{ project.bim_project_id }})</span>
                          </q-item-label>
                          <q-item-label
                            caption
                            class="item-type"
                            v-if="project.rowversion"
                          >
                            <span class="date-text">{{ formatDate(project.rowversion) }}</span>
                          </q-item-label>
                          <q-item-label
                            caption
                            class="item-caption"
                            v-if="project.comments"
                          >
                            {{ project.comments }}
                          </q-item-label>
                        </q-item-section>
                        <q-item-section side class="action-buttons">
                          <q-btn
                            dense
                            size="sm"
                            color="primary"
                            class="action-btn-more wrapper"
                            @click.stop
                          >
                            Действие
                            <q-icon name="expand_more" size="18px" class="q-ml-xs" />
                            <q-menu auto-close anchor="bottom right" self="top right">
                              <q-list style="min-width: 200px;">
                                <q-item clickable v-close-popup @click="openProjectDialog(project)">
                                  <q-item-section avatar>
                                    <q-icon name="edit" size="sm" color="primary" />
                                  </q-item-section>
                                  <q-item-section>Редактировать</q-item-section>
                                </q-item>
                                <q-item
                                  clickable
                                  v-close-popup
                                  @click="openCalcParamsDialog(project)"
                                  :disable="!project.project_type_id"
                                >
                                  <q-item-section avatar>
                                    <q-icon name="calculate" size="sm" color="secondary" />
                                  </q-item-section>
                                  <q-item-section>Параметры расчета</q-item-section>
                                </q-item>
                                <q-item clickable v-close-popup @click="openImportForProject(project)">
                                  <q-item-section avatar>
                                    <q-icon name="upload_file" size="sm" color="primary" />
                                  </q-item-section>
                                  <q-item-section>Импорт JSON</q-item-section>
                                </q-item>
                                <q-item clickable v-close-popup @click="openImportExcelForProject(project)">
                                  <q-item-section avatar>
                                    <q-icon name="table_chart" size="sm" color="primary" />
                                  </q-item-section>
                                  <q-item-section>Импорт Excel</q-item-section>
                                </q-item>
                                <q-item
                                  clickable
                                  v-close-popup
                                  @click="openImportToDigitalProject(project)"
                                >
                                  <q-item-section avatar>
                                    <q-icon name="link" size="sm" color="primary" />
                                  </q-item-section>
                                  <q-item-section>Импорт в цифровой проект</q-item-section>
                                </q-item>
                                <q-item clickable v-close-popup @click="confirmDeleteProject(project)">
                                  <q-item-section avatar>
                                    <q-icon name="delete" size="sm" color="negative" />
                                  </q-item-section>
                                  <q-item-section>Удалить</q-item-section>
                                </q-item>
                              </q-list>
                            </q-menu>
                          </q-btn>
                        </q-item-section>
                      </q-item>
                    </template>
                  </template>
                </template>
              </template>
            </template>
          </template>
        </div>
        <div
          v-else-if="!isLoadingProjects && projectTree.length === 0"
          class="empty-state"
        >
          <q-icon name="folder_off" color="grey-5" />
          <p>{{ projectSearchQuery ? 'Ничего не найдено' : 'Нет проектов' }}</p>
        </div>
        <div v-else class="loading-container">
          <q-spinner color="primary" size="30px" />
        </div>
      </div>

      <!-- Правая колонка с вкладками -->
      <div class="bim-projects__column details-column">
        <div v-if="!selectedProject" class="empty-state">
          <q-icon name="arrow_back" color="grey-5" />
          <p>← Выберите проект</p>
        </div>
        <div v-else class="tabs-container">
          <q-tabs
            v-model="activeTab"
            dense
            class="text-grey"
            active-color="primary"
            indicator-color="primary"
            align="left"
            @update:model-value="onTabChange"
          >
            <q-tab name="items">
              <template v-slot:default>
                <div class="row items-center no-wrap">
                  <div>Позиции</div>
                  <q-badge
                    v-if="items.length > 0"
                    color="primary"
                    :label="items.length"
                    class="q-ml-sm"
                  />
                </div>
              </template>
            </q-tab>
            <q-tab name="floors">
              <template v-slot:default>
                <div class="row items-center no-wrap">
                  <div>Этажи</div>
                  <q-badge
                    v-if="floorSchemes.length > 0"
                    color="primary"
                    :label="floorSchemes.length"
                    class="q-ml-sm"
                  />
                </div>
              </template>
            </q-tab>
          </q-tabs>

          <q-separator />

          <q-tab-panels v-model="activeTab" animated class="tab-panels">
            <!-- Вкладка: Позиции -->
            <q-tab-panel name="items" class="tab-panel">
              <div class="tab-header">
                <div v-if="categories.length > 0" class="tab-categories tab-categories--top">
                  <span class="tab-categories__label">Категория:</span>
                  <div class="tab-categories__chips">
                    <q-chip
                      clickable
                      dense
                      :color="selectedCategoryId == null ? 'primary' : 'grey-4'"
                      :text-color="selectedCategoryId == null ? 'white' : 'grey-8'"
                      @click="selectCategory(null)"
                    >
                      Все
                    </q-chip>
                    <q-chip
                      v-for="category in categories"
                      :key="category.bim_category_id"
                      clickable
                      dense
                      :color="Number(selectedCategoryId) === Number(category.bim_category_id) ? 'primary' : 'grey-4'"
                      :text-color="Number(selectedCategoryId) === Number(category.bim_category_id) ? 'white' : 'grey-8'"
                      @click="selectCategory(category.bim_category_id)"
                    >
                      {{ category.category_name }}
                    </q-chip>
                  </div>
                </div>
                <div class="tab-header-row tab-header-row--items-filters">
                  <div class="tab-filters-stack">
                    <div class="tab-filters-row">
                      <div class="filters__item filters__item--select">
                        <CustomSelect
                          :options="availableLevelsForSelect"
                          label="Этажи"
                          optionValueKey="value"
                          optionLabelKey="label"
                          :value="selectedLevelFilters"
                          @change="(value) => selectedLevelFilters = value"
                          :multiple="true"
                        >
                          <template v-slot:selected>
                            <span v-if="selectedLevelFilters.length === 0"></span>
                            <span
                              v-else-if="selectedLevelFilters.length === 1"
                              >{{ getLevelLabel(selectedLevelFilters[0]) }}</span
                            >
                            <span v-else
                              >Выбрано ({{ selectedLevelFilters.length }})</span
                            >
                          </template>
                        </CustomSelect>
                      </div>
                      <div class="filters__item filters__item--select filters__item--types">
                        <CustomSelect
                          :options="availableTypes"
                          label="Типы"
                          optionValueKey="bim_type_id"
                          optionLabelKey="type_name"
                          :value="selectedTypeFilters"
                          @change="(value) => selectedTypeFilters = value"
                          :multiple="true"
                        >
                          <template v-slot:selected>
                            <span v-if="selectedTypeFilters.length === 0"></span>
                            <span
                              v-else-if="selectedTypeFilters.length === 1"
                              >{{ getTypeLabel(selectedTypeFilters[0]) }}</span
                            >
                            <span v-else
                              >Выбрано ({{ selectedTypeFilters.length }})</span
                            >
                          </template>
                        </CustomSelect>
                      </div>
                      <div class="filters__item filters__item--select">
                        <CustomSelect
                          :options="lineFilterSelectOptions"
                          label="Линии"
                          optionValueKey="value"
                          optionLabelKey="label"
                          :value="selectedLineSlotFilters"
                          @change="(value) => selectedLineSlotFilters = value"
                          :multiple="true"
                        >
                          <template v-slot:selected>
                            <span v-if="selectedLineSlotFilters.length === 0"></span>
                            <span
                              v-else-if="selectedLineSlotFilters.length === 1"
                              >{{ getLineSlotLabel(selectedLineSlotFilters[0]) }}</span
                            >
                            <span v-else
                              >Выбрано ({{ selectedLineSlotFilters.length }})</span
                            >
                          </template>
                        </CustomSelect>
                      </div>
                      <div class="filters__item filters__item--select">
                        <CustomSelect
                          :options="availableMarksForSelect"
                          label="Марка"
                          optionValueKey="value"
                          optionLabelKey="label"
                          :value="selectedMarkFilters"
                          @change="(value) => selectedMarkFilters = value"
                          :multiple="true"
                        >
                          <template v-slot:selected>
                            <span v-if="selectedMarkFilters.length === 0"></span>
                            <span
                              v-else-if="selectedMarkFilters.length === 1"
                              >{{ getMarkLabel(selectedMarkFilters[0]) }}</span
                            >
                            <span v-else
                              >Выбрано ({{ selectedMarkFilters.length }})</span
                            >
                          </template>
                        </CustomSelect>
                      </div>
                    </div>
                    <div class="tab-filters-row tab-filters-row--second">
                      <div class="filters__item filters__item--search">
                        <CustomInput
                          label="Быстрый поиск"
                          type="text"
                          :value="itemSearchQuery"
                          @change_input="(value) => itemSearchQuery = value"
                          placeholder="Этаж, тип, Revit ID..."
                        />
                      </div>
                      <div class="filters__item filters__toggles">
                        <div class="filters__toggle-row">
                          <span class="text-caption">Только без линии</span>
                          <q-toggle
                            v-model="showOnlyWithoutLine"
                            dense
                            size="xs"
                            checked-icon="check"
                            unchecked-icon="clear"
                            color="warning"
                          >
                            <q-tooltip
                              >{{ showOnlyWithoutLine ? 'Показать все' : 'Показать только без линии' }}</q-tooltip
                            >
                          </q-toggle>
                        </div>
                        <div class="filters__toggle-row">
                          <span class="text-caption">Только ручные</span>
                          <q-toggle
                            v-model="showOnlyManual"
                            dense
                            size="xs"
                            checked-icon="check"
                            unchecked-icon="clear"
                            color="primary"
                          >
                            <q-tooltip
                              >{{ showOnlyManual ? 'Показать все' : 'Показать только ручные данные' }}</q-tooltip
                            >
                          </q-toggle>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="tab-actions">
                  <q-btn
                    v-if="selectedItems.length > 0"
                    icon="delete"
                    flat
                    dense
                    size="sm"
                    color="negative"
                    @click="confirmDeleteSelectedItems"
                  >
                    <q-tooltip
                      >Удалить выбранные ({{ selectedItems.length }})</q-tooltip
                    >
                  </q-btn>
                  <q-btn
                    v-if="selectedItems.length > 0"
                    icon="content_copy"
                    flat
                    dense
                    size="sm"
                    color="primary"
                    @click="openCopyToFloorsDialog"
                  >
                    <q-tooltip>Копировать на этажи ({{ selectedItems.length }})</q-tooltip>
                  </q-btn>
                  <q-btn
                    v-if="selectedItems.length > 0"
                    icon="layers"
                    flat
                    dense
                    size="sm"
                    color="primary"
                    @click="openChangeLevelDialog"
                  >
                    <q-tooltip>Изменить этаж ({{ selectedItems.length }})</q-tooltip>
                  </q-btn>
                  <q-btn
                    icon="download"
                    flat
                    dense
                    size="sm"
                    color="primary"
                    @click="exportItemsToExcel"
                    :disable="items.length === 0"
                  >
                    <q-tooltip>Экспорт в Excel</q-tooltip>
                  </q-btn>
                  <q-btn
                    icon="add"
                    flat
                    dense
                    size="sm"
                    color="primary"
                    @click="openFloorValueDialog()"
                  >
                    <q-tooltip>Добавить запись по этажу</q-tooltip>
                  </q-btn>
                </div>
                </div>
              </div>

              <div
                v-if="!isLoadingItems && items.length > 0"
                class="table-container"
              >
                <q-table
                  :rows="filteredItems"
                  :columns="itemColumns"
                  row-key="bim_project_item_id"
                  dense
                  flat
                  bordered
                  class="data-table"
                  virtual-scroll
                  :virtual-scroll-item-size="48"
                  :virtual-scroll-sticky-size-start="48"
                  :rows-per-page-options="[0]"
                  :pagination="{ rowsPerPage: 0 }"
                  style="max-height: calc(100vh - 350px);"
                >
                  <template v-slot:header="props">
                    <q-tr :props="props">
                      <q-th style="width: 40px;">
                        <q-checkbox
                          :model-value="allItemsSelected"
                          @update:model-value="toggleAllItems"
                          size="xs"
                          dense
                        />
                      </q-th>
                      <q-th
                        v-for="col in props.cols"
                        :key="col.name"
                        :props="props"
                        :class="{ 'bim-type-cell': col.name === 'bim_type_id', 'bim-mark-cell': col.name === 'bim_mark', 'bim-room-cell': col.name === 'bim_room_name' }"
                      >
                        {{ col.label }}
                      </q-th>
                    </q-tr>
                  </template>
                  <template v-slot:top-row>
                    <q-tr class="totals-row">
                      <q-td></q-td>
                      <q-td colspan="3"></q-td>
                      <q-td
                        class="text-right text-bold"
                        >{{ formatNumber(totalVolume) }}</q-td
                      >
                      <q-td
                        class="text-right text-bold"
                        >{{ formatNumber(totalCnt) }}</q-td
                      >
                      <q-td
                        class="text-right text-bold"
                        >{{ formatNumber(totalArea) }}</q-td
                      >
                      <q-td
                        class="text-right text-bold"
                        >{{ formatNumber(totalLength) }}</q-td
                      >
                      <q-td
                        class="text-right text-bold"
                        >{{ formatNumber(totalDiameter) }}</q-td
                      >
                      <q-td
                        class="text-right text-bold"
                        >{{ formatNumber(totalWeight) }}</q-td
                      >
                      <q-td
                        class="text-right text-bold"
                        >{{ formatNumber(totalEmptyCnt) }}</q-td
                      >
                      <q-td></q-td>
                      <q-td></q-td>
                      <q-td></q-td>
                      <q-td></q-td>
                    </q-tr>
                  </template>
                  <template v-slot:body="props">
                    <q-tr :props="props">
                      <q-td>
                        <q-checkbox
                          :model-value="selectedItems.includes(props.row.bim_project_item_id)"
                          @update:model-value="(val) => toggleItemSelection(props.row.bim_project_item_id, val)"
                          size="xs"
                          dense
                        />
                      </q-td>
                      <q-td key="bim_level_num" :props="props">
                        {{ props.row.bim_level_num || '-' }}
                      </q-td>
                      <q-td
                        key="bim_type_id"
                        :props="props"
                        class="bim-type-cell"
                      >
                        <div class="bim-type-cell__content">
                          <div class="row items-center no-wrap">
                            <div class="col">
                              {{ props.row.type_name || '-' }}
                            </div>
                            <div class="col-auto" v-if="props.row.bim_project_item_id">
                              <q-btn
                                dense
                                flat
                                size="xs"
                                icon="code"
                                color="primary"
                                @click.stop="openItemJsonDialog(props.row)"
                              >
                                <q-tooltip>Просмотреть JSON</q-tooltip>
                              </q-btn>
                            </div>
                          </div>
                          <div
                            class="text-caption text-grey-7"
                            v-if="props.row.bim_type_id"
                          >
                            ID: {{ props.row.bim_type_id }}
                          </div>
                          <div
                            class="text-caption"
                            :style="{ color: props.row.floor_param_type_name ? '#666' : '#f44336', fontWeight: props.row.floor_param_type_name ? 'normal' : '500' }"
                          >
                            <span
                              >Линия:
                              {{ props.row.floor_param_type_name || 'Не указано ⚠️' }}</span
                            >
                          </div>
                          <div
                            v-if="props.row.floor_param_type_name2"
                            class="text-caption"
                            style="color: #666;"
                          >
                            <span
                              >Линия 2:
                              {{ props.row.floor_param_type_name2 }}</span
                            >
                          </div>
                          <div
                            v-if="props.row.floor_param_type_name3"
                            class="text-caption"
                            style="color: #666;"
                          >
                            <span
                              >Линия 3:
                              {{ props.row.floor_param_type_name3 }}</span
                            >
                          </div>
                        </div>
                      </q-td>
                      <q-td key="bim_mark" :props="props" class="bim-mark-cell">
                        {{ props.row.bim_mark || '-' }}
                      </q-td>
                      <q-td key="bim_volume" :props="props" class="text-right">
                        {{ formatNumber(props.row.bim_volume) }}
                      </q-td>
                      <q-td key="bim_cnt" :props="props" class="text-right">
                        {{ formatNumber(props.row.bim_cnt) }}
                      </q-td>
                      <q-td key="bim_area" :props="props" class="text-right">
                        {{ formatNumber(props.row.bim_area) }}
                      </q-td>
                      <q-td key="bim_length" :props="props" class="text-right">
                        {{ formatNumber(props.row.bim_length) }}
                      </q-td>
                      <q-td
                        key="bim_diameter"
                        :props="props"
                        class="text-right"
                      >
                        {{ formatNumber(props.row.bim_diameter) }}
                      </q-td>
                      <q-td
                        key="bim_weight"
                        :props="props"
                        class="text-right"
                      >
                        {{ formatNumber(props.row.bim_weight) }}
                      </q-td>
                      <q-td key="bim_empty_cnt" :props="props" class="text-right">
                        {{ formatNumber(props.row.bim_empty_cnt) }}
                      </q-td>
                      <q-td key="bim_room_name" :props="props" class="bim-room-cell">
                        {{ props.row.bim_room_name || '—' }}
                      </q-td>
                      <q-td key="revit_id" :props="props">
                        {{ props.row.revit_id || '-' }}
                      </q-td>
                      <q-td key="is_manual" :props="props" class="text-center">
                        <q-icon
                          v-if="props.row.is_manual"
                          name="check_circle"
                          color="primary"
                          size="xs"
                        />
                        <span v-else class="text-grey-6">—</span>
                      </q-td>
                      <q-td key="actions" :props="props">
                        <q-btn
                          icon="edit"
                          flat
                          dense
                          round
                          size="xs"
                          color="primary"
                          @click="openItemDialog(props.row)"
                        >
                          <q-tooltip>Редактировать</q-tooltip>
                        </q-btn>
                      </q-td>
                    </q-tr>
                  </template>
                </q-table>
              </div>
              <div v-else-if="!isLoadingItems" class="empty-state-small">
                <q-icon name="inventory_2" color="grey-5" />
                <p>Нет позиций</p>
              </div>
              <div v-else class="loading-container-small">
                <q-spinner color="primary" size="30px" />
              </div>
            </q-tab-panel>

            <!-- Вкладка: Этажи -->
            <q-tab-panel name="floors" class="tab-panel">
              <div class="tab-header">
                <span class="tab-title">Схемы этажей</span>
                <div class="tab-actions">
                  <q-btn
                    v-if="selectedFloorSchemes.length > 0"
                    icon="delete"
                    flat
                    dense
                    size="sm"
                    color="negative"
                    @click="confirmDeleteSelectedFloorSchemes"
                  >
                    <q-tooltip
                      >Удалить выбранные ({{ selectedFloorSchemes.length


                      }})</q-tooltip
                    >
                  </q-btn>
                  <q-btn
                    icon="add"
                    flat
                    dense
                    size="sm"
                    color="primary"
                    @click="openFloorSchemeDialog()"
                  >
                    <q-tooltip>Добавить схему этажа</q-tooltip>
                  </q-btn>
                </div>
              </div>

              <div
                v-if="!isLoadingFloorSchemes && floorSchemes.length > 0"
                class="table-container"
              >
                <q-table
                  :rows="floorSchemes"
                  :columns="floorSchemeColumns"
                  row-key="bim_project_floor_scheme_id"
                  dense
                  flat
                  bordered
                  class="data-table"
                  :pagination="{ rowsPerPage: 0 }"
                  hide-pagination
                >
                  <template v-slot:header="props">
                    <q-tr :props="props">
                      <q-th style="width: 40px;">
                        <q-checkbox
                          :model-value="allFloorSchemesSelected"
                          @update:model-value="toggleAllFloorSchemes"
                          size="xs"
                          dense
                        />
                      </q-th>
                      <q-th
                        v-for="col in props.cols"
                        :key="col.name"
                        :props="props"
                      >
                        {{ col.label }}
                      </q-th>
                    </q-tr>
                  </template>
                  <template v-slot:body="props">
                    <q-tr :props="props">
                      <q-td>
                        <q-checkbox
                          :model-value="selectedFloorSchemes.includes(props.row.bim_project_floor_scheme_id)"
                          @update:model-value="(val) => toggleFloorSchemeSelection(props.row.bim_project_floor_scheme_id, val)"
                          size="xs"
                          dense
                        />
                      </q-td>
                      <q-td key="bim_level_num" :props="props">
                        {{ props.row.bim_level_num || '-' }}
                      </q-td>
                      <q-td key="file_name" :props="props">
                        <div>{{ props.row.file_name || '-' }}</div>
                        <div
                          v-if="props.row.file_path"
                          class="text-caption text-primary"
                        >
                          <a
                            :href="getImageUrl(props.row.file_path)"
                            target="_blank"
                            class="image-link"
                          >
                            Открыть файл
                          </a>
                        </div>
                      </q-td>
                      <q-td key="mime_type" :props="props">
                        {{ props.row.mime_type || '-' }}
                      </q-td>
                      <q-td key="preview" :props="props">
                        <img
                          v-if="props.row.file_path && isImageMimeType(props.row.mime_type)"
                          :src="getImageUrl(props.row.file_path)"
                          alt="Превью"
                          class="floor-scheme-preview"
                          @click="openImagePreview(props.row.file_path)"
                        />
                        <span v-else>-</span>
                      </q-td>
                      <q-td key="geometry" :props="props">
                        <q-btn
                          icon="map"
                          flat
                          dense
                          round
                          size="xs"
                          color="primary"
                          @click="openGeometryDialog(props.row)"
                        >
                          <q-tooltip>Показать схему</q-tooltip>
                        </q-btn>
                        <q-btn
                          v-if="props.row.file_path && isImageMimeType(props.row.mime_type)"
                          icon="crop"
                          flat
                          dense
                          round
                          size="xs"
                          color="secondary"
                          @click="openCropDialog(props.row)"
                          class="q-ml-xs"
                        >
                          <q-tooltip>Обрезать изображение</q-tooltip>
                        </q-btn>
                        <q-btn
                          icon="content_copy"
                          flat
                          dense
                          round
                          size="xs"
                          color="primary"
                          @click="openCopyFloorSchemeDialog(props.row)"
                          class="q-ml-xs"
                        >
                          <q-tooltip>Копировать на другие этажи</q-tooltip>
                        </q-btn>
                      </q-td>
                      <q-td key="actions" :props="props">
                        <q-btn
                          icon="edit"
                          flat
                          dense
                          round
                          size="xs"
                          color="primary"
                          @click="openFloorSchemeDialog(props.row)"
                        >
                          <q-tooltip>Редактировать</q-tooltip>
                        </q-btn>
                      </q-td>
                    </q-tr>
                  </template>
                </q-table>
              </div>
              <div v-else-if="!isLoadingFloorSchemes" class="empty-state-small">
                <q-icon name="layers" color="grey-5" />
                <p>Нет схем этажей</p>
              </div>
              <div v-else class="loading-container-small">
                <q-spinner color="primary" size="30px" />
              </div>
            </q-tab-panel>
          </q-tab-panels>
        </div>
      </div>
    </div>

    <!-- Диалог для добавления/редактирования проекта -->
    <q-dialog v-model="projectDialog" persistent>
      <q-card class="bim-modal">
        <q-card-section class="bim-modal__header">
          <div class="bim-modal__title">
            {{ editingProject ? 'Редактировать проект' : 'Добавить проект' }}
          </div>
          <q-btn
            icon="close"
            flat
            round
            dense
            v-close-popup
            class="bim-modal__close"
          />
        </q-card-section>

        <q-card-section class="bim-modal__content">
          <q-form @submit="saveProject" class="bim-form">
            <div class="bim-form__section">
              <div class="bim-form__item">
                <CustomInput
                  label="Название проекта"
                  type="text"
                  :required="true"
                  :value="projectForm.project_name"
                  @change_input="(value) => projectForm.project_name = value"
                  autofocus
                />
              </div>
              <div class="bim-form__item">
                <CustomSelect
                  label="ЖК"
                  :options="residentsOptions"
                  option-label-key="resident_name"
                  option-value-key="resident_id"
                  :value="projectForm.resident_id"
                  @change="(value) => projectForm.resident_id = value"
                  :loading="residentsLoading"
                  placeholder="Выберите ЖК (необязательно)"
                />
              </div>
              <div class="bim-form__item">
                <CustomSelect
                  label="Тип проекта"
                  :options="projectTypesOptions"
                  option-label-key="project_type_name"
                  option-value-key="project_type_id"
                  :value="projectForm.project_type_id"
                  @change="(value) => projectForm.project_type_id = value"
                  :loading="isLoadingProjectTypes"
                  placeholder="Выберите тип проекта"
                  :required="true"
                />
              </div>
              <div class="bim-form__item">
                <CustomInput
                  label="Номер блока"
                  type="number"
                  :value="projectForm.block_num"
                  @change_input="(value) => projectForm.block_num = value ? parseInt(value, 10) : null"
                  placeholder="1, 2, 3..."
                />
              </div>
              <div class="bim-form__item">
                <div class="customInput__label">Признак Revit</div>
                <q-option-group
                  v-model="projectForm.revit_type"
                  :options="revitTypeOptions"
                  type="radio"
                  color="primary"
                  dense
                  class="q-mt-xs"
                />
              </div>
              <div class="bim-form__item">
                <CustomInput
                  label="Комментарий"
                  type="textarea"
                  :value="projectForm.comments"
                  @change_input="(value) => projectForm.comments = value"
                />
              </div>
            </div>

            <div class="bim-modal__footer">
              <CustomButton
                @click="projectDialog = false"
                :disabled="isSaving"
                outlined
              >
                Отмена
              </CustomButton>
              <CustomButton type="submit" :loading="isSaving">
                {{ editingProject ? 'Сохранить' : 'Создать' }}
              </CustomButton>
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Диалог для calc параметров проекта -->
    <q-dialog v-model="calcParamsDialog" persistent>
      <q-card class="bim-modal">
        <q-card-section class="bim-modal__header">
          <div class="bim-modal__title">
            Параметры расчета
          </div>
          <q-btn
            icon="close"
            flat
            round
            dense
            v-close-popup
            class="bim-modal__close"
          />
        </q-card-section>

        <q-card-section class="bim-modal__content">
          <q-form @submit="saveCalcParams" class="bim-form">
            <div class="bim-form__section">
              <div v-if="calcTypes.length === 0" class="bim-form__item">
                <div style="text-align: center; padding: 20px; color: #999;">
                  Параметры расчета доступны только для проектов типа ROOF
                </div>
              </div>
              <div v-else class="bim-form__item" v-for="calcType in calcTypes" :key="calcType.bim_project_calc_type_id">
                <CustomInput
                  :label="calcType.bim_project_calc_type_name"
                  type="number"
                  :value="calcParamsForm[calcType.bim_project_calc_type_id] || ''"
                  @change_input="(value) => calcParamsForm[calcType.bim_project_calc_type_id] = value"
                  step="0.01"
                />
              </div>
            </div>

            <div class="bim-modal__footer">
              <CustomButton
                @click="calcParamsDialog = false"
                :disabled="isSavingCalcParams"
                outlined
              >
                Отмена
              </CustomButton>
              <CustomButton type="submit" :loading="isSavingCalcParams" :disabled="calcTypes.length === 0">
                Сохранить
              </CustomButton>
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Диалог для добавления/редактирования позиции -->
    <q-dialog v-model="itemDialog" persistent>
      <q-card class="bim-modal">
        <q-card-section class="bim-modal__header">
          <div class="bim-modal__title">
            {{ editingItem ? 'Редактировать позицию' : 'Добавить позицию' }}
          </div>
          <q-btn
            icon="close"
            flat
            round
            dense
            v-close-popup
            class="bim-modal__close"
          />
        </q-card-section>

        <q-card-section class="bim-modal__content">
          <q-form @submit="saveItem" class="bim-form">
            <div class="bim-form__section">
              <div class="bim-form__item">
                <CustomInput
                  label="Этаж"
                  type="text"
                  :value="itemForm.bim_level_num"
                  @change_input="(value) => itemForm.bim_level_num = value"
                />
              </div>
              <div class="bim-form__item">
                <CustomSelect
                  label="Тип"
                  :options="bimTypes"
                  option-label-key="type_name"
                  option-value-key="bim_type_id"
                  :value="itemForm.bim_type_id"
                  @change="(value) => itemForm.bim_type_id = value"
                  :loading="isLoadingTypes"
                  placeholder="Выберите тип"
                  clearable
                />
              </div>
              <div class="bim-form__item">
                <CustomInput
                  label="Объем"
                  type="number"
                  :value="itemForm.bim_volume"
                  @change_input="(value) => itemForm.bim_volume = value"
                  step="0.001"
                />
              </div>
              <div class="bim-form__item">
                <CustomInput
                  label="Кол-во"
                  type="number"
                  :value="itemForm.bim_cnt"
                  @change_input="(value) => itemForm.bim_cnt = value"
                  step="1"
                />
              </div>
              <div class="bim-form__item">
                <CustomInput
                  label="Площадь"
                  type="number"
                  :value="itemForm.bim_area"
                  @change_input="(value) => itemForm.bim_area = value"
                  step="0.001"
                />
              </div>
              <div class="bim-form__item">
                <CustomInput
                  label="Длина"
                  type="number"
                  :value="itemForm.bim_length"
                  @change_input="(value) => itemForm.bim_length = value"
                  step="0.001"
                />
              </div>
              <div class="bim-form__item">
                <CustomInput
                  label="Диаметр"
                  type="number"
                  :value="itemForm.bim_diameter"
                  @change_input="(value) => itemForm.bim_diameter = value"
                  step="0.001"
                />
              </div>
              <div class="bim-form__item">
                <CustomInput
                  label="Вес"
                  type="number"
                  :value="itemForm.bim_weight"
                  @change_input="(value) => itemForm.bim_weight = value"
                  step="0.001"
                />
              </div>
              <div class="bim-form__item">
                <CustomInput
                  label="Кол-во пустот"
                  type="number"
                  :value="itemForm.bim_empty_cnt"
                  @change_input="(value) => itemForm.bim_empty_cnt = value"
                  step="1"
                />
              </div>
            </div>

            <div class="bim-modal__footer">
              <CustomButton
                @click="itemDialog = false"
                :disabled="isSavingItem"
                outlined
              >
                Отмена
              </CustomButton>
              <CustomButton type="submit" :loading="isSavingItem">
                {{ editingItem ? 'Сохранить' : 'Создать' }}
              </CustomButton>
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Диалог: схема этажа (Этаж + Файл) — для вкладки «Этажи» -->
    <q-dialog v-model="floorSchemeDialog" persistent>
      <q-card class="bim-modal">
        <q-card-section class="bim-modal__header">
          <div class="bim-modal__title">
            {{ editingFloorScheme ? 'Редактировать схему этажа' : 'Добавить схему этажа' }}
          </div>
          <q-btn
            icon="close"
            flat
            round
            dense
            v-close-popup
            class="bim-modal__close"
          />
        </q-card-section>

        <q-card-section class="bim-modal__content">
          <q-form @submit="saveFloorScheme" class="bim-form">
            <div class="bim-form__section">
              <div class="bim-form__item">
                <CustomInput
                  label="Этаж"
                  type="text"
                  :value="floorSchemeForm.bim_level_num"
                  @change_input="(value) => floorSchemeForm.bim_level_num = value"
                />
              </div>
              <div class="bim-form__item">
                <div class="customInput__label">Файл схемы этажа</div>
                <q-file
                  v-model="floorSchemeForm.file"
                  outlined
                  dense
                  accept="image/*"
                  @update:model-value="handleFloorSchemeFileChange"
                  :hint="floorSchemeForm.file_name || 'Выберите изображение схемы этажа'"
                  class="file-input"
                >
                  <template v-slot:prepend>
                    <q-icon name="attach_file" />
                  </template>
                  <template v-slot:append v-if="floorSchemeForm.file">
                    <q-icon
                      name="close"
                      @click.stop="clearFloorSchemeFile"
                      class="cursor-pointer"
                    />
                  </template>
                </q-file>
              </div>
              <div class="bim-form__item" v-if="floorSchemeForm.file_name">
                <CustomInput
                  label="Путь к файлу (опционально)"
                  type="text"
                  :value="floorSchemeForm.file_path"
                  @change_input="(value) => floorSchemeForm.file_path = value"
                  placeholder="/путь/к/схемам/"
                />
              </div>
              <div v-if="floorSchemeForm.file_name" class="file-info">
                <div class="file-info-item">
                  <strong>Имя файла:</strong> {{ floorSchemeForm.file_name }}
                </div>
                <div class="file-info-item">
                  <strong>MIME тип:</strong> {{ floorSchemeForm.mime_type }}
                </div>
              </div>
            </div>

            <div class="bim-modal__footer">
              <CustomButton
                @click="floorSchemeDialog = false"
                :disabled="isSavingFloorScheme"
                outlined
              >
                Отмена
              </CustomButton>
              <CustomButton type="submit" :loading="isSavingFloorScheme">
                {{ editingFloorScheme ? 'Сохранить' : 'Создать' }}
              </CustomButton>
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Диалог: запись значения параметра по этажу (для кнопки «Добавить» во вкладке «Позиции») -->
    <q-dialog v-model="floorValueDialog" persistent>
      <q-card class="bim-modal">
        <q-card-section class="bim-modal__header">
          <div class="bim-modal__title">Добавить запись по этажу</div>
          <q-btn
            icon="close"
            flat
            round
            dense
            v-close-popup
            class="bim-modal__close"
          />
        </q-card-section>

        <q-card-section class="bim-modal__content">
          <q-form @submit="saveFloorValue" class="bim-form">
            <div class="bim-form__section">
              <div class="bim-form__item">
                <CustomSelect
                  label="Категория"
                  :options="allCategoriesList"
                  option-label-key="category_name"
                  option-value-key="bim_category_id"
                  :value="floorValueForm.bim_category_id"
                  @change="(value) => onFloorValueCategoryChange(value)"
                  placeholder="Выберите категорию"
                />
              </div>
              <div class="bim-form__item">
                <CustomInput
                  label="Этаж"
                  type="text"
                  :value="floorValueForm.bim_level_num"
                  @change_input="(value) => floorValueForm.bim_level_num = value"
                />
              </div>
              <div class="bim-form__item">
                <CustomSelect
                  label="Тип"
                  :options="floorSchemeTypesOptions"
                  option-label-key="type_name"
                  option-value-key="bim_type_id"
                  :value="floorValueForm.bim_type_id"
                  @change="(value) => floorValueForm.bim_type_id = value"
                  :loading="isLoadingFloorSchemeTypes"
                  placeholder="Выберите тип"
                />
              </div>
              <div class="bim-form__item bim-form__item--param-value">
                <div class="param-value-rows">
                  <div
                    v-for="(row, idx) in floorValueForm.paramRows"
                    :key="idx"
                    class="param-value-row"
                  >
                    <div class="param-value-row__select">
                      <CustomSelect
                        :label="idx === 0 ? 'Параметр' : ''"
                        :options="floorSchemeParamOptions"
                        option-label-key="label"
                        option-value-key="key"
                        :value="row.param_key"
                        @change="(value) => row.param_key = value"
                        placeholder="Параметр"
                      />
                    </div>
                    <div class="param-value-row__input" v-if="row.param_key">
                      <CustomInput
                        :label="idx === 0 ? getFloorSchemeParamLabel(row.param_key) : ''"
                        type="number"
                        step="any"
                        :value="row.param_value"
                        @change_input="(value) => row.param_value = value"
                        placeholder="Значение"
                      />
                    </div>
                    <div class="param-value-row__actions" v-if="floorValueForm.paramRows.length > 1">
                      <q-btn
                        icon="remove"
                        flat
                        round
                        dense
                        size="sm"
                        color="grey"
                        @click="removeFloorValueParamRow(idx)"
                      >
                        <q-tooltip>Удалить строку</q-tooltip>
                      </q-btn>
                    </div>
                  </div>
                </div>
                <div class="param-value-add-row">
                  <q-btn
                    icon="add"
                    flat
                    dense
                    size="sm"
                    color="primary"
                    @click="addFloorValueParamRow"
                  >
                    Добавить параметр
                  </q-btn>
                </div>
              </div>
            </div>

            <div class="bim-modal__footer">
              <CustomButton
                @click="floorValueDialog = false"
                :disabled="isSavingFloorValue"
                outlined
              >
                Отмена
              </CustomButton>
              <CustomButton type="submit" :loading="isSavingFloorValue">
                Создать
              </CustomButton>
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>
    <!-- Прогресс-бар для группового удаления -->
    <q-dialog v-model="deletionProgressDialog" persistent>
      <q-card style="min-width: 400px">
        <q-card-section>
          <div class="text-h6">Удаление записей</div>
        </q-card-section>

        <q-card-section>
          <div style="margin-bottom: 10px;">
            Удалено: {{ deletionProgress.current }} из
            {{ deletionProgress.total }}
          </div>
          <q-linear-progress
            :value="deletionProgress.current / deletionProgress.total"
            color="primary"
            size="20px"
            class="q-mt-sm"
          >
            <div class="absolute-full flex flex-center">
              <q-badge
                color="white"
                text-color="primary"
                :label="`${Math.round((deletionProgress.current / deletionProgress.total) * 100)}%`"
              />
            </div>
          </q-linear-progress>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Диалог подтверждения -->
    <q-dialog v-model="confirmDialog.show" persistent>
      <q-card style="min-width: 350px">
        <q-card-section>
          <div class="text-h6">{{ confirmDialog.title }}</div>
        </q-card-section>

        <q-card-section>
          {{ confirmDialog.message }}
        </q-card-section>

        <q-card-actions align="right">
          <q-btn
            flat
            label="Отмена"
            color="grey"
            @click="confirmDialog.show = false"
          />
          <q-btn
            unelevated
            label="Удалить"
            color="negative"
            @click="onConfirmDelete"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог: копировать выбранные позиции на этажи -->
    <q-dialog v-model="copyToFloorsDialog" persistent>
      <q-card class="bim-modal" style="min-width: 400px">
        <q-card-section class="bim-modal__header">
          <div class="bim-modal__title">Копировать на этажи</div>
          <q-btn icon="close" flat round dense v-close-popup class="bim-modal__close" />
        </q-card-section>
        <q-card-section class="bim-modal__content">
          <p class="text-body2 q-mb-md">
            Выбрано позиций: <strong>{{ selectedItems.length }}</strong>. Укажите этажи, на которые нужно скопировать данные.
          </p>
          <div class="copy-floors-inputs">
            <div
              v-for="(floorVal, idx) in copyToFloorsInputs"
              :key="idx"
              class="copy-floors-input-row"
            >
              <div class="copy-floors-input-cell">
                <CustomInput
                  :label="idx === 0 ? 'Этаж' : ''"
                  type="text"
                  :value="floorVal"
                  @change_input="(value) => copyToFloorsInputs[idx] = value"
                  :placeholder="idx === 0 ? 'Номер этажа' : 'Ещё этаж'"
                />
              </div>
              <q-btn
                v-if="copyToFloorsInputs.length > 1"
                icon="remove"
                flat
                round
                dense
                size="sm"
                color="grey"
                @click="removeCopyToFloorsInput(idx)"
              >
                <q-tooltip>Удалить</q-tooltip>
              </q-btn>
            </div>
          </div>
          <div class="copy-floors-add-row">
            <q-btn
              icon="add"
              flat
              dense
              size="sm"
              color="primary"
              @click="addCopyToFloorsInput"
            >
              Добавить этаж
            </q-btn>
          </div>
        </q-card-section>
        <q-card-actions align="right" class="q-pa-md">
          <q-btn flat label="Отмена" color="grey" :disabled="isCopyingToFloors" @click="copyToFloorsDialog = false" />
          <q-btn
            unelevated
            label="Копировать"
            color="primary"
            :loading="isCopyingToFloors"
            @click="copySelectedItemsToFloors"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог: изменить этаж выбранных позиций -->
    <q-dialog v-model="changeLevelDialog" persistent>
      <q-card class="bim-modal" style="min-width: 360px">
        <q-card-section class="bim-modal__header">
          <div class="bim-modal__title">Изменить этаж</div>
          <q-btn icon="close" flat round dense v-close-popup class="bim-modal__close" />
        </q-card-section>
        <q-card-section class="bim-modal__content">
          <p class="text-body2 q-mb-md">
            Выбрано позиций: <strong>{{ selectedItems.length }}</strong>. Укажите новый этаж для всех выбранных.
          </p>
          <div class="q-mt-sm">
            <CustomInput
              label="Этаж"
              type="text"
              :value="changeLevelValue"
              @change_input="(value) => (changeLevelValue = value)"
              placeholder="Номер этажа"
            />
          </div>
        </q-card-section>
        <q-card-actions align="right" class="q-pa-md">
          <q-btn flat label="Отмена" color="grey" :disabled="isUpdatingLevel" @click="changeLevelDialog = false" />
          <q-btn
            unelevated
            label="Изменить"
            color="primary"
            :loading="isUpdatingLevel"
            @click="submitChangeLevel"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог: копировать схему этажа на другие этажи -->
    <q-dialog v-model="copyFloorSchemeDialog" persistent>
      <q-card class="bim-modal" style="min-width: 400px">
        <q-card-section class="bim-modal__header">
          <div class="bim-modal__title">Копировать схему на этажи</div>
          <q-btn icon="close" flat round dense v-close-popup class="bim-modal__close" />
        </q-card-section>
        <q-card-section class="bim-modal__content">
          <p class="text-body2 q-mb-md" v-if="copyFloorSchemeSource">
            Схема этажа <strong>{{ copyFloorSchemeSource.bim_level_num }}</strong>. Выберите этажи, на которые скопировать.
          </p>
          <div class="copy-floors-list q-mt-sm" v-if="copyFloorSchemeTargetOptions.length">
            <div
              v-for="opt in copyFloorSchemeTargetOptions"
              :key="opt.bim_project_floor_scheme_id"
              class="row items-center q-mb-xs"
            >
              <q-checkbox
                :model-value="copyFloorSchemeTargetIds.includes(opt.bim_project_floor_scheme_id)"
                @update:model-value="(val) => toggleCopyFloorSchemeTarget(opt.bim_project_floor_scheme_id, val)"
              />
              <span class="q-ml-sm">Этаж {{ opt.bim_level_num }}</span>
            </div>
          </div>
          <p v-else class="text-grey">Нет других схем этажей для копирования.</p>
        </q-card-section>
        <q-card-actions align="right" class="q-pa-md">
          <q-btn flat label="Отмена" color="grey" :disabled="isCopyingFloorScheme" @click="copyFloorSchemeDialog = false" />
          <q-btn
            unelevated
            label="Копировать"
            color="primary"
            :loading="isCopyingFloorScheme"
            :disable="!copyFloorSchemeTargetIds.length"
            @click="copyFloorSchemeToFloorsSubmit"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог просмотра JSON параметров позиции -->
    <q-dialog v-model="itemJsonDialog.show">
      <q-card style="min-width: 600px; max-width: 90vw; max-height: 80vh; display: flex; flex-direction: column;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">
            {{ itemJsonDialog.title || 'JSON параметров позиции' }}
          </div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section
          class="q-pt-md"
          style="flex: 1 1 auto; overflow: auto; background: #1e1e1e; color: #eee;"
        >
          <div v-if="itemJsonDialog.isLoading" class="flex flex-center" style="min-height: 150px;">
            <q-spinner color="primary" size="40px" />
          </div>
          <pre
            v-else
            style="margin: 0; font-family: Menlo, Monaco, Consolas, 'Courier New', monospace; font-size: 12px; white-space: pre;"
          >{{ itemJsonDialog.content }}</pre>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn label="Закрыть" flat v-close-popup />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог просмотра изображения -->
    <q-dialog v-model="imagePreviewDialog">
      <q-card style="min-width: 600px; max-width: 90vw;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Просмотр схемы этажа</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section
          class="q-pt-none"
          style="max-height: 70vh; overflow: auto;"
        >
          <img
            :src="imagePreviewUrl"
            alt="Схема этажа"
            style="width: 100%; height: auto;"
          />
        </q-card-section>

        <q-card-actions align="right">
          <q-btn
            label="Открыть в новой вкладке"
            color="primary"
            :href="imagePreviewUrl"
            target="_blank"
            flat
          />
          <q-btn label="Закрыть" v-close-popup flat />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Модалка: удалить или сохранить ручные данные -->
    <q-dialog v-model="showManualConflictDialog" persistent>
      <q-card class="bim-modal" style="min-width: 420px">
        <q-card-section class="bim-modal__header">
          <div class="bim-modal__title">В проекте есть данные с ручным вводом</div>
          <q-btn icon="close" flat round dense v-close-popup class="bim-modal__close" />
        </q-card-section>
        <q-card-section class="bim-modal__content">
          <p class="text-body2 text-grey-8 q-mb-none">
            Выберите: удалить ручные данные или сохранить их и продолжить импорт?
          </p>
        </q-card-section>
        <q-card-actions align="right" class="q-px-md q-pb-md">
          <q-btn label="Отмена" flat v-close-popup :disable="isImportingJson" />
          <q-btn
            label="Сохранить"
            color="primary"
            unelevated
            :loading="isImportingJson"
            @click="onManualConflictChoice(false)"
          />
          <q-btn
            label="Удалить"
            color="negative"
            flat
            :loading="isImportingJson"
            @click="onManualConflictChoice(true)"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог импорта JSON -->
    <q-dialog v-model="importJsonDialog" persistent>
      <q-card style="min-width: 500px">
        <q-card-section class="bim-modal__header">
          <div class="bim-modal__title">
            Импорт JSON в "{{ selectedProject?.project_name }}"
          </div>
          <q-btn
            icon="close"
            flat
            round
            dense
            v-close-popup
            class="bim-modal__close"
          />
        </q-card-section>

        <q-card-section class="bim-modal__content">
          <div class="import-json-info">
            <p style="margin-bottom: 16px; color: #666;">
              Добавьте один или несколько наборов файлов JSON или ZIP.
            </p>

            <div
              v-for="(group, groupIndex) in importFileGroups"
              :key="groupIndex"
              class="q-mb-md"
              style="border: 1px solid #e0e0e0; border-radius: 4px; padding: 12px;"
            >
              <div class="row justify-end q-mb-sm" v-if="importFileGroups.length > 1">
                <q-btn
                  icon="delete"
                  flat
                  round
                  color="negative"
                  size="sm"
                  @click="removeImportGroup(groupIndex)"
                >
                  <q-tooltip>Удалить набор</q-tooltip>
                </q-btn>
              </div>

              <q-file
                v-model="group.files"
                outlined
                dense
                label="Выберите JSON или ZIP файлы"
                accept=".json,.zip"
                max-file-size="52428800"
                multiple
                @rejected="onFileRejected"
              >
                <template v-slot:prepend>
                  <q-icon name="attach_file" />
                </template>
                <template
                  v-slot:append
                  v-if="group.files && group.files.length > 0"
                >
                  <q-icon
                    name="cancel"
                    @click.stop="clearGroupFiles(groupIndex)"
                    class="cursor-pointer"
                  />
                </template>
              </q-file>

              <div
                v-if="group.files && group.files.length > 0"
                class="file-info q-mt-md"
              >
                <div
                  v-for="(file, index) in group.files"
                  :key="index"
                  class="file-info-item q-mb-sm"
                  style="display: flex; justify-content: space-between; align-items: center; padding: 8px; background: #f5f5f5; border-radius: 4px;"
                >
                  <div>
                    <div><strong>Файл:</strong> {{ file.name }}</div>
                    <div>
                      <strong>Размер:</strong> {{ formatFileSize(file.size) }}
                    </div>
                  </div>
                  <q-icon
                    name="cancel"
                    @click.stop="removeFileFromGroup(groupIndex, index)"
                    class="cursor-pointer"
                    size="20px"
                    color="red"
                  >
                    <q-tooltip>Удалить файл</q-tooltip>
                  </q-icon>
                </div>
              </div>
            </div>

            <!-- Список загруженных категорий -->
            <div v-if="projectCategories.length > 0" class="q-mt-lg project-categories-block">
              <div class="project-categories-header">
                <q-icon name="info" size="18px" color="red" class="q-mr-sm" />
                <span class="text-subtitle2" style="font-weight: 600; color: #d32f2f;">
                  Загруженные категории в проекте:
                </span>
              </div>
              <div class="project-categories-list">
                <div
                  v-for="category in projectCategories"
                  :key="category.bim_category_id"
                  class="project-category-item"
                >
                  <div class="category-name">{{ category.category_name }}</div>
                  <div class="category-details">
                    <div v-if="category.last_load_date" class="category-date">
                      {{ formatDate(category.last_load_date) }}
                    </div>
                    <div v-else class="category-date">—</div>
                    <div v-if="category.employee_fio" class="category-employee">
                      {{ category.employee_fio }}
                    </div>
                    <div v-else class="category-employee">—</div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else-if="!isLoadingProjectCategories" class="q-mt-lg">
              <div class="text-caption text-grey-7">
                В проекте пока нет загруженных категорий
              </div>
            </div>
            <q-btn
              flat
              icon="add"
              label="Добавить набор файлов"
              color="primary"
              class="q-mb-md"
              @click="addImportGroup"
            />

            <div class="q-mt-lg">
              <q-checkbox
                v-model="isDeletePrev"
                label="Удалить предыдущую загрузку"
                color="primary"
              />

              <div v-if="!isDeletePrev" class="warning-message q-mt-sm">
                <q-icon name="warning" color="warning" size="20px" />
                <span>
                  <strong>Внимание:</strong> Существующие данные в проекте
                  останутся, и новые данные добавятся. При повторной загрузке
                  одного и того же JSON файла создадутся дубликаты.
                </span>
              </div>
            </div>

            <div
              v-if="importResultStats && importResultStats.length > 0"
              class="q-mt-lg"
            >
              <div class="text-subtitle1 q-mb-sm">
                {{ importResultMessage || 'Результаты импорта' }}
              </div>
              <div
                v-for="(row, idx) in importResultStats"
                :key="idx"
                class="q-mb-sm"
                style="border: 1px solid #e0e0e0; border-radius: 4px; padding: 8px 12px;"
              >
                <div style="font-weight: 600; margin-bottom: 4px;">
                  {{ idx + 1 }}. {{ row.file_name }}
                </div>
                <div style="font-size: 13px; color: #555;">
                  <div>
                    <strong>Тип:</strong>
                    {{ row.type === 'rebar' ? 'Арматура' : 'Остальные' }}
                  </div>
                  <div>
                    <strong>Функция:</strong>
                    {{ row.sp_name }}
                  </div>
                  <div>
                    <strong>Время выполнения:</strong>
                    {{ row.duration_ms }} мс
                  </div>
                </div>
                <div
                  v-if="row.warning"
                  style="margin-top: 8px; padding: 8px; background: #fff3e0; border-left: 3px solid #ff9800; border-radius: 2px; font-size: 13px; color: #e65100; white-space: pre-line;"
                >
                  <q-icon name="warning" size="16px" color="orange" class="q-mr-xs" />
                  {{ row.warning }}
                </div>
              </div>
            </div>
          </div>
        </q-card-section>

        <q-card-actions align="right" class="q-px-md q-pb-md">
          <q-btn label="Отмена" flat v-close-popup :disable="isImportingJson" />
          <q-btn
            label="Импортировать"
            color="primary"
            unelevated
            @click="importJson"
            :loading="isImportingJson"
            :disable="!hasImportFiles"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог обрезки изображения -->
    <q-dialog v-model="cropDialog" maximized>
      <q-card>
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">
            Обрезка изображения схемы этажа
            {{ cropCurrentScheme?.bim_level_num }}
          </div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section class="q-pt-none crop-section">
          <div class="crop-instructions">
            <q-icon name="info" color="primary" size="20px" />
            <span
              >Выделите область для обрезки, зажав левую кнопку мыши и перетащив
              курсор</span
            >
          </div>
          <div class="crop-canvas-container">
            <canvas
              ref="cropCanvas"
              class="crop-canvas"
              @mousedown="startCrop"
              @mousemove="updateCrop"
              @mouseup="endCrop"
            ></canvas>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn label="Отмена" flat v-close-popup />
          <q-btn
            label="Сохранить обрезку"
            color="primary"
            @click="saveCroppedImage"
            :disable="!isCropSelected"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог просмотра геометрии этажа -->
    <q-dialog v-model="geometryDialog" maximized>
      <q-card>
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Схема этажа {{ currentFloorLevel }}</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section class="q-pt-none geometry-section">
          <div v-if="isLoadingGeometry" class="full-height flex flex-center">
            <q-spinner color="primary" size="50px" />
          </div>
          <div
            v-else-if="geometryData.length === 0"
            class="full-height flex flex-center text-grey-6"
          >
            Нет данных геометрии для этого этажа
          </div>
          <div v-else class="geometry-layout">
            <!-- Canvas слева -->
            <div ref="canvasContainer" class="canvas-container">
              <canvas
                ref="geometryCanvas"
                class="geometry-canvas"
                @wheel.prevent="handleCanvasWheel"
                @mousedown="handleCanvasMouseDown"
                @mousemove="handleCanvasMouseMove"
                @mouseup="handleCanvasMouseUp"
                @mouseleave="handleCanvasMouseLeave"
              ></canvas>
              <div
                v-if="showTooltip && hoveredLine"
                class="geometry-tooltip"
                :style="{left: tooltipX + 'px', top: tooltipY + 'px'}"
              >
                <div class="tooltip-title">{{ hoveredLine.typeName }}</div>
                <div class="tooltip-item">
                  <strong>Revit ID:</strong> {{ hoveredLine.revitId }}
                </div>
                <div class="tooltip-item" v-if="hoveredLine.bimInfo">
                  <strong>BIM Info:</strong> {{ hoveredLine.bimInfo }}
                </div>
              </div>
              <div v-if="coordinatesMode" class="coordinates-display">
                <div class="coordinates-title">
                  <q-icon name="gps_fixed" size="16px" />
                  <span>Координаты</span>
                </div>
                <div class="coordinates-values">
                  <div class="coordinate-item">
                    <span class="coordinate-label">X:</span>
                    <span
                      class="coordinate-value"
                      >{{ currentCoordinates.x.toFixed(3) }}</span
                    >
                  </div>
                  <div class="coordinate-item">
                    <span class="coordinate-label">Y:</span>
                    <span
                      class="coordinate-value"
                      >{{ currentCoordinates.y.toFixed(3) }}</span
                    >
                  </div>
                </div>
              </div>
              <div class="canvas-controls">
                <q-btn
                  icon="zoom_in"
                  round
                  dense
                  size="sm"
                  color="primary"
                  @click="zoomIn"
                >
                  <q-tooltip>Увеличить</q-tooltip>
                </q-btn>
                <q-btn
                  icon="zoom_out"
                  round
                  dense
                  size="sm"
                  color="primary"
                  @click="zoomOut"
                  class="q-ml-xs"
                >
                  <q-tooltip>Уменьшить</q-tooltip>
                </q-btn>
                <q-btn
                  icon="center_focus_strong"
                  round
                  dense
                  size="sm"
                  color="primary"
                  @click="resetZoom"
                  class="q-ml-xs"
                >
                  <q-tooltip>Сбросить масштаб</q-tooltip>
                </q-btn>
                <q-separator vertical class="q-mx-xs" />
                <q-btn
                  :icon="coordinatesMode ? 'gps_fixed' : 'gps_not_fixed'"
                  round
                  dense
                  size="sm"
                  :color="coordinatesMode ? 'positive' : 'grey-7'"
                  @click="coordinatesMode = !coordinatesMode"
                  class="q-ml-xs"
                >
                  <q-tooltip
                    >{{ coordinatesMode ? 'Выключить режим координат' : 'Включить режим координат' }}</q-tooltip
                  >
                </q-btn>
                <q-separator vertical class="q-mx-xs" v-if="floorImage" />
                <q-btn
                  v-if="floorImage && !calibrationMode"
                  icon="tune"
                  round
                  dense
                  size="sm"
                  color="secondary"
                  @click="enterCalibrationMode"
                  class="q-ml-xs"
                >
                  <q-tooltip>Калибровка обложки</q-tooltip>
                </q-btn>
                <q-btn
                  v-if="calibrationMode"
                  icon="check"
                  round
                  dense
                  size="sm"
                  color="positive"
                  @click="saveCalibration"
                  class="q-ml-xs"
                >
                  <q-tooltip>Сохранить</q-tooltip>
                </q-btn>
                <q-btn
                  v-if="calibrationMode"
                  icon="close"
                  round
                  dense
                  size="sm"
                  color="negative"
                  @click="cancelCalibration"
                  class="q-ml-xs"
                >
                  <q-tooltip>Отмена</q-tooltip>
                </q-btn>
              </div>
              <div v-if="floorImage" class="opacity-control">
                <div class="opacity-label">
                  <q-icon name="image" size="16px" />
                  <span>Прозрачность обложки</span>
                </div>
                <q-slider
                  v-model="floorImageOpacity"
                  :min="0"
                  :max="1"
                  :step="0.1"
                  color="primary"
                  @update:model-value="drawGeometry"
                  dense
                  class="opacity-slider"
                />
                <span class="opacity-value"
                  >{{ Math.round(floorImageOpacity * 100) }}%</span
                >
              </div>
            </div>

            <!-- Панель типов справа -->
            <div class="types-panel">
              <div class="types-header">
                <div class="text-subtitle1 text-weight-medium">
                  Типы конструкций
                </div>
                <q-input
                  v-model="typeSearchQuery"
                  placeholder="Поиск типа..."
                  dense
                  outlined
                  clearable
                  class="q-mt-sm"
                >
                  <template v-slot:prepend>
                    <q-icon name="search" />
                  </template>
                </q-input>
                <q-btn
                  :label="allTypesSelected ? 'Снять все' : 'Выбрать все'"
                  flat
                  dense
                  size="sm"
                  color="primary"
                  @click="toggleAllTypes"
                  class="q-mt-xs"
                />
              </div>
              <q-separator />
              <div class="types-list">
                <div
                  v-for="type in filteredAndSortedTypes"
                  :key="type.bimTypeId"
                  class="type-item"
                >
                  <q-checkbox
                    :model-value="selectedTypes.includes(type.bimTypeId)"
                    @update:model-value="toggleType(type.bimTypeId)"
                    dense
                  >
                    <template v-slot:default>
                      <div class="type-item-content">
                        <div class="type-name">{{ type.typeName }}</div>
                        <div class="type-stats">
                          <div class="type-volume">
                            {{ formatVolume(type.volume) }} м³
                          </div>
                          <div class="type-count">{{ type.count }} эл.</div>
                        </div>
                      </div>
                    </template>
                  </q-checkbox>
                </div>
              </div>
            </div>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn label="Закрыть" flat v-close-popup />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог импорта Excel (CSV / xlsx) -->
    <q-dialog v-model="importExcelDialog" persistent>
      <q-card style="min-width: 500px">
        <q-card-section class="bim-modal__header">
          <div class="bim-modal__title">
            Импорт Excel в «{{ selectedProject?.project_name }}»
          </div>
          <q-btn
            icon="close"
            flat
            round
            dense
            v-close-popup
            class="bim-modal__close"
          />
        </q-card-section>

        <q-card-section class="bim-modal__content">
          <div class="import-json-info">
            <p style="margin-bottom: 16px; color: #666;">
              Загрузите один файл: Excel (.xlsx, .xls) или CSV с разделителем «;» или «,» (как в экспорте позиций).
            </p>

            <q-file
              v-model="excelImportFile"
              outlined
              dense
              label="Файл Excel или CSV"
              accept=".xlsx,.xls,.csv"
              max-file-size="52428800"
              @rejected="onFileRejected"
            >
              <template v-slot:prepend>
                <q-icon name="attach_file" />
              </template>
              <template v-slot:append v-if="excelImportFile">
                <q-icon
                  name="cancel"
                  @click.stop="excelImportFile = null"
                  class="cursor-pointer"
                />
              </template>
            </q-file>

            <div
              v-if="excelImportFile"
              class="file-info q-mt-md"
            >
              <div
                class="file-info-item"
                style="display: flex; justify-content: space-between; padding: 8px; background: #f5f5f5; border-radius: 4px;"
              >
                <div>
                  <div><strong>Файл:</strong> {{ excelImportFile.name }}</div>
                  <div>
                    <strong>Размер:</strong> {{ formatFileSize(excelImportFile.size) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Загруженные категории -->
            <div v-if="isLoadingProjectCategories" class="q-mt-lg flex flex-center">
              <q-spinner color="primary" size="24px" />
            </div>
            <div v-else-if="projectCategories.length > 0" class="q-mt-lg project-categories-block">
              <div class="project-categories-header">
                <q-icon name="info" size="18px" color="red" class="q-mr-sm" />
                <span class="text-subtitle2" style="font-weight: 600; color: #d32f2f;">
                  Загруженные категории в проекте:
                </span>
              </div>
              <div class="project-categories-list">
                <div
                  v-for="category in projectCategories"
                  :key="category.bim_category_id"
                  class="project-category-item"
                >
                  <div class="category-name">{{ category.category_name }}</div>
                  <div class="category-details">
                    <div v-if="category.last_load_date" class="category-date">
                      {{ formatDate(category.last_load_date) }}
                    </div>
                    <div v-else class="category-date">—</div>
                    <div v-if="category.employee_fio" class="category-employee">
                      {{ category.employee_fio }}
                    </div>
                    <div v-else class="category-employee">—</div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="q-mt-lg">
              <div class="text-caption text-grey-7">
                В проекте пока нет загруженных категорий
              </div>
            </div>

            <div
              v-if="importExcelResultStats && importExcelResultStats.length > 0"
              class="q-mt-lg"
            >
              <div class="text-subtitle1 q-mb-sm">
                {{ importExcelResultMessage || 'Результаты импорта' }}
              </div>
              <div
                v-for="(row, idx) in importExcelResultStats"
                :key="idx"
                class="q-mb-sm"
                style="border: 1px solid #e0e0e0; border-radius: 4px; padding: 8px 12px;"
              >
                <div style="font-weight: 600; margin-bottom: 4px;">
                  {{ idx + 1 }}. {{ row.file_name }}
                </div>
                <div style="font-size: 13px; color: #555;">
                  <div>
                    <strong>Тип:</strong> Excel
                  </div>
                  <div>
                    <strong>Функция:</strong>
                    {{ row.sp_name }}
                  </div>
                  <div>
                    <strong>Время выполнения:</strong>
                    {{ row.duration_ms }} мс
                  </div>
                </div>
              </div>
            </div>
          </div>
        </q-card-section>

        <q-card-actions align="right" class="q-px-md q-pb-md">
          <q-btn label="Отмена" flat v-close-popup :disable="isImportingExcel" />
          <q-btn
            label="Импортировать"
            color="primary"
            unelevated
            @click="importProjectExcel"
            :loading="isImportingExcel"
            :disable="!excelImportFile"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import CustomInput from "@/components/form/CustomInput";
import CustomButton from "@/components/common/CustomButton.vue";
import CustomSelect from "@/components/form/CustomSelect";
import cookiesService from "@/services/cookiesService";
import instance from "@/services/api";
import {
  getBimProjects,
  saveBimProject,
  deleteBimProject,
  importBimProjectJson,
  importBimProjectExcel,
  getBimProjectItems,
  getBimProjectItemJson,
  saveBimProjectItem,
  deleteBimProjectItem,
  deleteBimProjectItems,
  copyBimProjectItemsToFloors,
  updateBimProjectItemsLevel,
  copyBimProjectFloorSchemeToFloors,
  getBimProjectFloorSchemes,
  saveBimProjectFloorScheme,
  deleteBimProjectFloorScheme,
  getBimProjectFloorSchemeGeometry,
  saveBimProjectFloorSchemeCalibration,
  getBimCategories,
  getBimTypes,
  getBimTypesByCategory,
  getBimParams,
  getBimProjectCalcParams,
  saveBimProjectCalcParams
} from "@/views/BimProjects/services";
import { getProjectTypes } from "@/views/BuildingSettings/services";
import { getResidentsForSelect } from "@/views/BimProjects/services";

const cookiesServiceInstance = cookiesService.getService();

export default {
  name: "BimProjects",
  components: {
    CustomInput,
    CustomButton,
    CustomSelect
  },
  data() {
    return {
      projects: [],
      items: [],
      floorSchemes: [],
      categories: [],
      bimTypes: [],
      bimParams: [],
      selectedLevelFilters: [],
      selectedTypeFilters: [],
      /** Слоты линий (1/2/3) — соответствуют floor_param_type_id и «Линия» в ячейке типа */
      selectedLineSlotFilters: [],
      showOnlyWithoutLine: false,
      showOnlyManual: false,
      selectedProject: null,
      selectedProjectTypeId: null,
      selectedCategoryId: null,
      selectedProjects: [],
      selectedItems: [],
      selectedFloorSchemes: [],
      isLoadingProjects: false,
      isLoadingItems: false,
      isLoadingFloorSchemes: false,
      isLoadingCategories: false,
      isLoadingTypes: false,
      isLoadingParams: false,
      isLoadingProjectTypes: false,
      isSaving: false,
      isSavingItem: false,
      isSavingFloorScheme: false,
      isSavingFloorValue: false,
      projectDialog: false,
      itemDialog: false,
      floorSchemeDialog: false,
      floorValueDialog: false,
      copyToFloorsDialog: false,
      copyToFloorsInputs: [''],
      isCopyingToFloors: false,
      changeLevelDialog: false,
      changeLevelValue: '',
      isUpdatingLevel: false,
      copyFloorSchemeDialog: false,
      copyFloorSchemeSource: null,
      copyFloorSchemeTargetIds: [],
      isCopyingFloorScheme: false,
      importJsonDialog: false,
      importExcelDialog: false,
      manualConflictImportKind: 'json',
      excelImportFile: null,
      isImportingExcel: false,
      importExcelResultMessage: '',
      importExcelResultStats: [],
      showManualConflictDialog: false,
      editingProject: null,
      editingItem: null,
      editingFloorScheme: null,
      importFileGroups: [
        {
          type: 'other',
          files: []
        }
      ],
      isImportingJson: false,
      importResultStats: [],
      importResultMessage: '',
      isDeletePrev: true,
      projectCategories: [],
      isLoadingProjectCategories: false,
      projectForm: {
        project_name: '',
        comments: '',
        project_type_id: null,
        revit_type: 1,
        resident_id: null,
        block_num: null
      },
      residents: [],
      residentsLoading: false,
      expandedTreeKeys: {},
      revitTypeOptions: [
        { label: 'КЖ', value: 1 },
        { label: 'АР', value: 2 }
      ],
      calcTypes: [],
      isLoadingCalcTypes: false,
      calcParamsDialog: false,
      calcParamsForm: {},
      editingCalcProject: null,
      isSavingCalcParams: false,
      itemForm: {
        bim_level_num: null,
        bim_type_id: null,
        bim_volume: null,
        bim_cnt: null,
        bim_area: null,
        bim_length: null,
        bim_diameter: null,
        bim_weight: null,
        bim_empty_cnt: null
      },
      floorSchemeForm: {
        bim_level_num: null,
        file_path: '',
        file_name: '',
        mime_type: '',
        file: null
      },
      floorValueForm: {
        bim_category_id: null,
        bim_level_num: null,
        bim_type_id: null,
        paramRows: [{ param_key: null, param_value: null }]
      },
      floorSchemeTypesOptions: [],
      floorSchemeItemsOptions: [],
      selectedFloorSchemeItem: null,
      isLoadingFloorSchemeTypes: false,
      isLoadingFloorSchemeItems: false,
      floorSchemeParamOptions: [
        { key: 'bim_cnt', label: 'Количество' },
        { key: 'bim_area', label: 'Площадь' },
        { key: 'bim_volume', label: 'Объём' },
        { key: 'bim_length', label: 'Длина' },
        { key: 'bim_diameter', label: 'Диаметр' },
        { key: 'bim_weight', label: 'Вес' },
        { key: 'bim_empty_cnt', label: 'Кол-во пустот' }
      ],
      /** Полный список категорий для диалога «Добавить запись по этажу» (без привязки к проекту) */
      allCategoriesList: [],
      activeTab: 'items',
      projectSearchQuery: '',
      itemSearchQuery: '',
      selectedMarkFilters: [],
      deletionProgressDialog: false,
      deletionProgress: {
        current: 0,
        total: 0
      },
      confirmDialog: {
        show: false,
        title: '',
        message: '',
        onConfirm: null
      },
      itemJsonDialog: {
        show: false,
        title: '',
        content: '',
        isLoading: false
      },
      itemColumns: [
        { name: 'bim_level_num', label: 'Этаж', field: 'bim_level_num', align: 'left' },
        { name: 'bim_type_id', label: 'Тип', field: 'type_name', align: 'left', style: 'min-width: 220px; max-width: min(400px, 40vw);' },
        { name: 'bim_mark', label: 'Марка', field: 'bim_mark', align: 'left' },
        { name: 'bim_volume', label: 'Объем', field: 'bim_volume', align: 'right' },
        { name: 'bim_cnt', label: 'Кол-во', field: 'bim_cnt', align: 'right' },
        { name: 'bim_area', label: 'Площадь', field: 'bim_area', align: 'right' },
        { name: 'bim_length', label: 'Длина', field: 'bim_length', align: 'right' },
        { name: 'bim_diameter', label: 'Диаметр', field: 'bim_diameter', align: 'right' },
        { name: 'bim_weight', label: 'Вес', field: 'bim_weight', align: 'right' },
        { name: 'bim_empty_cnt', label: 'Кол-во пустот', field: 'bim_empty_cnt', align: 'right' },
        { name: 'bim_room_name', label: 'Комнаты', field: 'bim_room_name', align: 'left', style: 'min-width: 180px; max-width: min(320px, 30vw);' },
        { name: 'revit_id', label: 'Revit ID', field: 'revit_id', align: 'left' },
        { name: 'is_manual', label: 'Руч.', field: 'is_manual', align: 'center' },
        { name: 'actions', label: 'Действия', field: 'actions', align: 'center' }
      ],
      floorSchemeColumns: [
        { name: 'bim_level_num', label: 'Этаж', field: 'bim_level_num', align: 'left' },
        { name: 'file_name', label: 'Имя файла', field: 'file_name', align: 'left' },
        { name: 'mime_type', label: 'Тип', field: 'mime_type', align: 'left' },
        { name: 'preview', label: 'Превью', field: 'file_path', align: 'center' },
        { name: 'geometry', label: 'Схема', field: 'geometry', align: 'center' },
        { name: 'actions', label: 'Действия', field: 'actions', align: 'center' }
      ],
      imagePreviewDialog: false,
      imagePreviewUrl: '',
      geometryDialog: false,
      geometryData: [],
      isLoadingGeometry: false,
      currentFloorLevel: null,
      // Параметры для зума и перетаскивания
      canvasZoom: 1,
      canvasOffsetX: 0,
      canvasOffsetY: 0,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      geometryLines: [],
      geometryBounds: null,
      hoveredLine: null,
      projectTypesOptions: [],
      tooltipX: 0,
      tooltipY: 0,
      showTooltip: false,
      typeSummary: {},
      selectedTypes: [],
      allTypesSelected: true,
      typeSearchQuery: '',
      coordinatesMode: false,
      currentCoordinates: { x: 0, y: 0 },
      floorImage: null,
      floorImageOpacity: 0.5,
      isLoadingFloorImage: false,
      calibrationMode: false,
      isDraggingImage: false,
      isResizingImage: false,
      resizeHandle: null,
      imageOffsetX: 0,
      imageOffsetY: 0,
      imageWidth: 0,
      imageHeight: 0,
      currentFloorSchemeId: null,
      resizeStartX: 0,
      resizeStartY: 0,
      resizeStartImageX: 0,
      resizeStartImageY: 0,
      resizeStartImageWidth: 0,
      resizeStartImageHeight: 0,
      // Crop mode
      cropDialog: false,
      cropImage: null,
      cropImageUrl: '',
      cropCanvas: null,
      cropCtx: null,
      isCropping: false,
      cropStartX: 0,
      cropStartY: 0,
      cropEndX: 0,
      cropEndY: 0,
      cropCurrentScheme: null,
      // Copy to floors
      copyToFloorsDialog: false,
      isCopyingToFloors: false,
      changeLevelDialog: false,
      changeLevelValue: '',
      isUpdatingLevel: false,
      // Инициализация из URL
      initialProjectId: null
    };
  },
  computed: {
    filteredProjects() {
      if (!this.projectSearchQuery) return this.projects;
      const query = this.projectSearchQuery.toLowerCase().trim();
      return this.projects.filter(project =>
        project.project_name.toLowerCase().includes(query) ||
        (project.comments && project.comments.toLowerCase().includes(query)) ||
        (project.resident_name && project.resident_name.toLowerCase().includes(query)) ||
        (project.bim_project_id != null && String(project.bim_project_id).toLowerCase().includes(query))
      );
    },
    projectTree() {
      const list = this.filteredProjects;
      const residentMap = new Map();
      const NO_RESIDENT_KEY = '__no_resident__';
      const NO_TYPE_KEY = '__no_type__';
      const NO_BLOCK_KEY = '__no_block__';
      for (const p of list) {
        const hasResident = p.resident_id != null && p.resident_id !== '' && p.resident_id !== 0;
        const rKey = hasResident ? `r_${p.resident_id}` : NO_RESIDENT_KEY;
        const rLabel = hasResident ? (p.resident_name || `ЖК #${p.resident_id}`) : 'Проекты без ЖК';
        if (!residentMap.has(rKey)) residentMap.set(rKey, { label: rLabel, typeMap: new Map() });
        const hasType = p.project_type_id != null && p.project_type_id !== '' && p.project_type_id !== 0;
        const typeKey = hasType ? `t_${p.project_type_id}` : NO_TYPE_KEY;
        const typeLabel = hasType ? (p.project_type_name || 'Без типа проекта') : 'Без типа проекта';
        const typeNode = residentMap.get(rKey).typeMap;
        if (!typeNode.has(typeKey)) typeNode.set(typeKey, { label: typeLabel, blockMap: new Map() });
        const hasBlock = p.block_num != null && p.block_num !== '' && p.block_num !== 0;
        const bKey = hasBlock ? `b_${p.block_num}` : NO_BLOCK_KEY;
        const bLabel = hasBlock ? `Блок № ${p.block_num}` : 'Без блоков';
        const blockNode = typeNode.get(typeKey).blockMap;
        if (!blockNode.has(bKey)) blockNode.set(bKey, { label: bLabel, projects: [] });
        blockNode.get(bKey).projects.push(p);
      }
      return Array.from(residentMap.entries()).map(([rKey, r]) => ({
        key: rKey,
        label: r.label,
        children: Array.from(r.typeMap.entries()).map(([tKey, t]) => ({
          key: `${rKey}|${tKey}`,
          label: t.label,
          children: Array.from(t.blockMap.entries()).map(([bKey, b]) => ({
            key: `${rKey}|${tKey}|${bKey}`,
            label: b.label,
            projects: b.projects
          }))
        }))
      }));
    },
    residentsOptions() {
      return (this.residents || []).map(r => ({
        resident_id: r.resident_id,
        resident_name: r.resident_name
      }));
    },
    allProjectsSelected() {
      return this.filteredProjects.length > 0 && this.selectedProjects.length === this.filteredProjects.length;
    },
    allItemsSelected() {
      return this.filteredItems.length > 0 && this.selectedItems.length === this.filteredItems.length;
    },
    allFloorSchemesSelected() {
      return this.floorSchemes.length > 0 && this.selectedFloorSchemes.length === this.floorSchemes.length;
    },
    totalVolume() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.bim_volume) || 0), 0);
    },
    totalCnt() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.bim_cnt) || 0), 0);
    },
    totalArea() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.bim_area) || 0), 0);
    },
    totalLength() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.bim_length) || 0), 0);
    },
    totalDiameter() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.bim_diameter) || 0), 0);
    },
    totalWeight() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.bim_weight) || 0), 0);
    },
    totalEmptyCnt() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.bim_empty_cnt) || 0), 0);
    },
    filteredAndSortedTypes() {
      // Получаем массив типов с данными
      const typesArray = Object.entries(this.typeSummary).map(([bimTypeId, data]) => ({
        bimTypeId,
        ...data
      }));

      // Фильтруем по поисковому запросу
      let filtered = typesArray;
      if (this.typeSearchQuery) {
        const query = this.typeSearchQuery.toLowerCase();
        filtered = typesArray.filter(type =>
          type.typeName.toLowerCase().includes(query)
        );
      }

      // Сортируем по имени типа
      return filtered.sort((a, b) =>
        a.typeName.localeCompare(b.typeName, 'ru')
      );
    },
    hasImportFiles() {
      if (!this.importFileGroups || this.importFileGroups.length === 0) {
        return false;
      }
      return this.importFileGroups.some(
        (group) => group.files && group.files.length > 0
      );
    },
    isAnyProjectImporting() {
      return this.isImportingJson || this.isImportingExcel;
    },
    copyFloorSchemeTargetOptions() {
      if (!this.copyFloorSchemeSource || !this.floorSchemes.length) return [];
      const sourceId = this.copyFloorSchemeSource.bim_project_floor_scheme_id;
      return this.floorSchemes.filter(
        (s) => s.bim_project_floor_scheme_id !== sourceId
      );
    },
    isRoofProjectType() {
      if (!this.projectForm.project_type_id) return false;
      const projectType = this.projectTypesOptions.find(
        pt => pt.project_type_id === this.projectForm.project_type_id
      );
      return projectType && projectType.project_type_code === 'ROOF';
    },
    availableLevels() {
      const levels = [...new Set(this.items.map(item => item.bim_level_num).filter(level => level !== null && level !== undefined))];
      return levels.sort((a, b) => {
        const numA = parseFloat(a);
        const numB = parseFloat(b);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        return String(a).localeCompare(String(b));
      });
    },
    availableLevelsForSelect() {
      return this.availableLevels.map(level => ({
        value: level,
        label: String(level)
      }));
    },
    availableTypes() {
      const typesMap = new Map();
      this.items.forEach(item => {
        if (item.bim_type_id && item.type_name) {
          typesMap.set(item.bim_type_id, {
            bim_type_id: item.bim_type_id,
            type_name: item.type_name
          });
        }
      });
      return Array.from(typesMap.values()).sort((a, b) => a.type_name.localeCompare(b.type_name));
    },
    /** Названия линий из текущих позиций (floor_param_type_name / 2 / 3); без данных — fallback «Линия N» */
    lineFilterSelectOptions() {
      const map = new Map();
      const add = (slot, id, name) => {
        const i = id != null && id !== '' ? Number(id) : null;
        const n = (name || '').trim() || null;
        if (i == null && !n) return;
        const spec = { s: slot, i, n };
        const value = JSON.stringify(spec);
        if (map.has(value)) return;
        const label = n || (i != null ? `Тип линии id ${i}` : `Линия ${slot}`);
        map.set(value, { value, label, s: slot, i, n });
      };
      (this.items || []).forEach((item) => {
        add(1, item.floor_param_type_id, item.floor_param_type_name);
        add(2, item.floor_param_type_id2, item.floor_param_type_name2);
        add(3, item.floor_param_type_id3, item.floor_param_type_name3);
      });
      let list = Array.from(map.values());
      const labelCount = {};
      list.forEach((o) => {
        labelCount[o.label] = (labelCount[o.label] || 0) + 1;
      });
      list = list.map((o) => ({
        ...o,
        label: labelCount[o.label] > 1 ? `${o.label} (линия ${o.s})` : o.label,
      }));
      list.sort((a, b) => a.label.localeCompare(b.label, 'ru'));
      if (list.length === 0) {
        return [1, 2, 3].map((s) => ({
          value: JSON.stringify({ s, fb: true }),
          label: `Линия ${s}`,
          s,
          fb: true,
        }));
      }
      return list;
    },
    availableMarksForSelect() {
      const marksSet = new Set();
      this.items.forEach(item => {
        const m = item.bim_mark == null ? '' : String(item.bim_mark).trim();
        marksSet.add(m);
      });
      const list = Array.from(marksSet).sort((a, b) => {
        if (a === '') return 1;
        if (b === '') return -1;
        return a.localeCompare(b);
      });
      return list.map(m => ({
        value: m,
        label: m === '' ? '(Пусто)' : m
      }));
    },
    filteredItems() {
      let filtered = this.items;

      // Фильтр по этажам
      if (this.selectedLevelFilters.length > 0) {
        filtered = filtered.filter(item =>
          this.selectedLevelFilters.includes(item.bim_level_num)
        );
      }

      // Фильтр по типам
      if (this.selectedTypeFilters.length > 0) {
        filtered = filtered.filter(item =>
          this.selectedTypeFilters.includes(item.bim_type_id)
        );
      }

      // Фильтр по линиям (по названию / floor_param_type_id в слоте 1–3)
      if (this.selectedLineSlotFilters.length > 0) {
        filtered = filtered.filter((item) =>
          this.selectedLineSlotFilters.some((raw) => this.lineFilterItemMatches(item, raw))
        );
      }

      // Фильтр "Только без линии"
      if (this.showOnlyWithoutLine) {
        filtered = filtered.filter(item => !item.floor_param_type_name);
      }

      // Фильтр "Только ручные" (по полю is_manual)
      if (this.showOnlyManual) {
        filtered = filtered.filter(item => !!item.is_manual);
      }

      // Фильтр по марке (выпадающий список)
      if (this.selectedMarkFilters.length > 0) {
        const markSet = new Set(this.selectedMarkFilters);
        filtered = filtered.filter(item => {
          const m = item.bim_mark == null ? '' : String(item.bim_mark).trim();
          return markSet.has(m);
        });
      }

      // Быстрый поиск по основным полям
      if (this.itemSearchQuery) {
        const q = this.itemSearchQuery.toLowerCase();
        filtered = filtered.filter(item => {
          return (
            String(item.bim_level_num || '').toLowerCase().includes(q) ||
            (item.type_name || '').toLowerCase().includes(q) ||
            String(item.floor_param_type_name || '').toLowerCase().includes(q) ||
            String(item.floor_param_type_name2 || '').toLowerCase().includes(q) ||
            String(item.floor_param_type_name3 || '').toLowerCase().includes(q) ||
            String(item.bim_mark || '').toLowerCase().includes(q) ||
            String(item.bim_volume || '').toLowerCase().includes(q) ||
            String(item.bim_cnt || '').toLowerCase().includes(q) ||
            String(item.bim_area || '').toLowerCase().includes(q) ||
            String(item.bim_length || '').toLowerCase().includes(q) ||
            String(item.bim_diameter || '').toLowerCase().includes(q) ||
            String(item.bim_weight || '').toLowerCase().includes(q) ||
            String(item.revit_id || '').toLowerCase().includes(q) ||
            (item.is_manual ? 'руч ручные' : '').toLowerCase().includes(q)
          );
        });
      }

      return filtered;
    },
    isCropSelected() {
      return this.cropStartX !== 0 || this.cropStartY !== 0 || this.cropEndX !== 0 || this.cropEndY !== 0;
    },
  },
  methods: {
    async openItemJsonDialog(item) {
      if (!item || !item.bim_project_item_id) {
        return;
      }

      this.itemJsonDialog.show = true;
      this.itemJsonDialog.isLoading = true;
      this.itemJsonDialog.content = '';
      this.itemJsonDialog.title = item.type_name || `Позиция #${item.bim_project_item_id}`;

      const res = await getBimProjectItemJson(item.bim_project_item_id);

      this.itemJsonDialog.isLoading = false;

      if (!res) {
        this.itemJsonDialog.content = 'Нет данных JSON для этой позиции';
        return;
      }

      if (typeof res === 'string') {
        // Пытаемся красиво отформатировать, если это валидный JSON
        try {
          const parsed = JSON.parse(res);
          this.itemJsonDialog.content = JSON.stringify(parsed, null, 2);
        } catch (e) {
          // Если строка не является валидным JSON, показываем как есть
          this.itemJsonDialog.content = res;
        }
      } else {
        // На случай, если тип поля в БД json/jsonb и axios вернул объект — форматируем с отступами
        this.itemJsonDialog.content = JSON.stringify(res, null, 2);
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      try {
        // Если уже в формате DD.MM.YYYY — возвращаем как есть
        if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateString)) return dateString;

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
      } catch (e) {
        return '';
      }
    },
    onTabChange(tabName) {
      // Загружаем позиции только при переключении на вкладку "Позиции"
      if (tabName === 'items' && this.selectedProject && this.items.length === 0) {
        this.loadItems(this.selectedProject.bim_project_id);
      }
    },
    async loadProjects() {
      this.isLoadingProjects = true;
      const params = {};
      if (this.selectedProjectTypeId) {
        params.project_type_id = this.selectedProjectTypeId;
      }
      const res = await getBimProjects(params);
      this.isLoadingProjects = false;
      if (!res) return;
      this.projects = res;
    },
    async loadProjectsSilently() {
      const params = {};
      if (this.selectedProjectTypeId) {
        params.project_type_id = this.selectedProjectTypeId;
      }
      const res = await getBimProjects(params);
      if (!res) return;
      this.projects = res;
    },
    /**
     * Категории проекта. По умолчанию — «Все» (bim_category_id не передаётся в API).
     * Конкретная категория только если задана в URL или выбрана чипом.
     */
    async loadCategories({ selectDefaultAndLoadItems = false } = {}) {
      if (!this.selectedProject || !this.selectedProject.bim_project_id) {
        this.categories = [];
        this.selectedCategoryId = null;
        return;
      }

      this.isLoadingCategories = true;
      const res = await getBimCategories(this.selectedProject.bim_project_id);
      this.isLoadingCategories = false;
      this.categories = res || [];

      if (!this.categories.length) {
        this.selectedCategoryId = null;
        this.updateRouteQuery({ bim_category_id: undefined });
        if (selectDefaultAndLoadItems) {
          this.items = [];
        }
        return;
      }

      let categoryIdToSelect = this.selectedCategoryId;
      if (categoryIdToSelect != null) {
        const inList = this.categories.some(
          (c) => Number(c.bim_category_id) === Number(categoryIdToSelect)
        );
        if (!inList) {
          categoryIdToSelect = null;
        }
      }

      this.selectedCategoryId = categoryIdToSelect;
      this.updateRouteQuery({
        bim_category_id: categoryIdToSelect != null ? categoryIdToSelect : undefined,
      });

      if (selectDefaultAndLoadItems && this.selectedProject) {
        await this.loadItems(this.selectedProject.bim_project_id);
      }
    },
    selectCategory(categoryId) {
      this.selectedCategoryId = categoryId;
      this.onCategoryChange();
    },
    onCategoryChange() {
      // При изменении категории перезагружаем позиции
      this.selectedLevelFilters = [];
      this.selectedTypeFilters = [];
      this.selectedLineSlotFilters = [];
      this.selectedMarkFilters = [];
      this.showOnlyWithoutLine = false;
      this.showOnlyManual = false;

      // Обновляем категорию в URL
      this.updateRouteQuery({
        bim_category_id: this.selectedCategoryId || undefined
      });

      if (this.selectedProject) {
        this.loadItems(this.selectedProject.bim_project_id);
      }
    },
    async loadProjectTypes() {
      this.isLoadingProjectTypes = true;
      const res = await getProjectTypes();
      this.isLoadingProjectTypes = false;
      this.projectTypesOptions = res || [];
    },
    onProjectTypeChange() {
      // Сохраняем выбранный тип проекта в URL и сбрасываем выбранный проект
      this.selectedProject = null;
      this.selectedProjects = [];
      this.items = [];
      this.floorSchemes = [];

      this.updateRouteQuery({
        project_type_id: this.selectedProjectTypeId || undefined,
        project_id: undefined
      });

      this.loadProjects();
    },
    async selectProject(project) {
      this.selectedProject = project;
      this.activeTab = 'items';
      this.selectedItems = [];
      this.selectedFloorSchemes = [];
      this.items = []; // Сбрасываем позиции при переключении проекта

      const routeProjectIdRaw = this.$route.query.project_id;
      const routeProjectId =
        routeProjectIdRaw != null && routeProjectIdRaw !== ''
          ? Number(routeProjectIdRaw)
          : null;
      const routeMatchesThisProject =
        routeProjectId != null &&
        !Number.isNaN(routeProjectId) &&
        routeProjectId === Number(project.bim_project_id);
      const routeCat = this.$route.query.bim_category_id;
      if (
        routeMatchesThisProject &&
        routeCat != null &&
        routeCat !== ''
      ) {
        this.selectedCategoryId = Number(routeCat);
      } else {
        this.selectedCategoryId = null;
      }

      await this.loadFloorSchemes(project.bim_project_id);

      this.updateRouteQuery({
        project_id: project.bim_project_id,
        bim_category_id:
          this.selectedCategoryId != null ? this.selectedCategoryId : undefined
      });

      // Категории + позиции (по умолчанию «Все»; категория из URL — только если project_id в query совпадает)
      await this.loadCategories({ selectDefaultAndLoadItems: true });
    },
    async loadItems(projectId) {
      this.isLoadingItems = true;
      const res = await getBimProjectItems(projectId, this.selectedCategoryId);
      this.isLoadingItems = false;
      if (!res) return;
      this.items = res;
    },
    async loadItemsSilently(projectId) {
      const res = await getBimProjectItems(projectId, this.selectedCategoryId);
      if (!res) return;
      this.items = res;
    },
    async loadFloorSchemes(projectId) {
      this.isLoadingFloorSchemes = true;
      const res = await getBimProjectFloorSchemes(projectId);
      this.isLoadingFloorSchemes = false;
      if (!res) return;
      this.floorSchemes = res;
    },
    async loadFloorSchemesSilently(projectId) {
      const res = await getBimProjectFloorSchemes(projectId);
      if (!res) return;
      this.floorSchemes = res;
    },
    /**
     * Инициализируем фильтры и выбранный проект из query-параметров URL
     */
    initializeFromURL() {
      const query = this.$route.query || {};

      if (query.project_type_id) {
        this.selectedProjectTypeId = Number(query.project_type_id);
      }

      if (query.bim_category_id) {
        this.selectedCategoryId = Number(query.bim_category_id);
      }

      if (query.project_id) {
        const numericId = Number(query.project_id);
        if (!Number.isNaN(numericId)) {
          this.initialProjectId = numericId;
        }
      }
    },
    /**
     * После загрузки проектов выбираем проект из URL (если он есть)
     */
    initializeSelectedProjectFromURL() {
      const projectIdFromQuery =
        this.initialProjectId ||
        (this.$route.query.project_id ? Number(this.$route.query.project_id) : null);

      if (!projectIdFromQuery || !this.projects || !this.projects.length) return;

      const project = this.projects.find(
        (p) => Number(p.bim_project_id) === projectIdFromQuery
      );

      if (project) {
        this.selectProject(project);
      }
    },
    /**
     * Обновляем query-параметры URL без смены страницы
     */
    updateRouteQuery(patch) {
      const currentQuery = this.$route.query || {};
      const newQuery = { ...currentQuery };

      Object.keys(patch).forEach((key) => {
        const value = patch[key];
        if (value === undefined || value === null || value === "") {
          delete newQuery[key];
        } else {
          newQuery[key] = String(value);
        }
      });

      // Если ничего не изменилось — не трогаем роутер
      const currentString = JSON.stringify(currentQuery);
      const nextString = JSON.stringify(newQuery);
      if (currentString === nextString) return;

      this.$router.push({ query: newQuery });
    },
    toggleProjectSelection(projectId, selected) {
      if (selected) {
        if (!this.selectedProjects.includes(projectId)) {
          this.selectedProjects.push(projectId);
        }
      } else {
        const index = this.selectedProjects.indexOf(projectId);
        if (index > -1) {
          this.selectedProjects.splice(index, 1);
        }
      }
    },
    toggleAllProjects(selected) {
      if (selected) {
        const filteredIds = this.filteredProjects.map(p => p.bim_project_id);
        this.selectedProjects = [...new Set([...this.selectedProjects, ...filteredIds])];
      } else {
        const filteredIds = this.filteredProjects.map(p => p.bim_project_id);
        this.selectedProjects = this.selectedProjects.filter(id => !filteredIds.includes(id));
      }
    },
    toggleItemSelection(itemId, selected) {
      if (selected) {
        if (!this.selectedItems.includes(itemId)) {
          this.selectedItems.push(itemId);
        }
      } else {
        const index = this.selectedItems.indexOf(itemId);
        if (index > -1) {
          this.selectedItems.splice(index, 1);
        }
      }
    },
    toggleAllItems(selected) {
      if (selected) {
        const filteredIds = this.filteredItems.map(item => item.bim_project_item_id);
        this.selectedItems = [...new Set([...this.selectedItems, ...filteredIds])];
      } else {
        const filteredIds = this.filteredItems.map(item => item.bim_project_item_id);
        this.selectedItems = this.selectedItems.filter(id => !filteredIds.includes(id));
      }
    },
    toggleFloorSchemeSelection(schemeId, selected) {
      if (selected) {
        if (!this.selectedFloorSchemes.includes(schemeId)) {
          this.selectedFloorSchemes.push(schemeId);
        }
      } else {
        const index = this.selectedFloorSchemes.indexOf(schemeId);
        if (index > -1) {
          this.selectedFloorSchemes.splice(index, 1);
        }
      }
    },
    toggleAllFloorSchemes(selected) {
      if (selected) {
        this.selectedFloorSchemes = this.floorSchemes.map(scheme => scheme.bim_project_floor_scheme_id);
      } else {
        this.selectedFloorSchemes = [];
      }
    },
    toggleTreeKey(key) {
      this.expandedTreeKeys = { ...this.expandedTreeKeys, [key]: !this.expandedTreeKeys[key] };
    },
    async loadResidents() {
      if (this.residentsLoading) return;
      this.residentsLoading = true;
      try {
        const list = await getResidentsForSelect();
        this.residents = list || [];
      } catch (e) {
        console.error('Ошибка загрузки ЖК:', e);
        this.residents = [];
      } finally {
        this.residentsLoading = false;
      }
    },
    async openProjectDialog(project = null) {
      this.editingProject = project;
      if (project) {
        this.projectForm = {
          project_name: project.project_name,
          comments: project.comments || '',
          project_type_id: project.project_type_id || null,
          revit_type: project.revit_type || 1,
          resident_id: project.resident_id ?? null,
          block_num: project.block_num ?? null
        };
      } else {
        this.projectForm = {
          project_name: '',
          comments: '',
          project_type_id: this.selectedProjectTypeId || null,
          revit_type: 1,
          resident_id: null,
          block_num: null
        };
      }
      await this.loadResidents();
      this.projectDialog = true;
    },
    isRoofProjectTypeById(project_type_id) {
      if (!project_type_id) return false;
      // Если projectTypesOptions еще не загружены, пытаемся загрузить
      if (!this.projectTypesOptions || this.projectTypesOptions.length === 0) {
        return false;
      }
      const projectType = this.projectTypesOptions.find(
        pt => pt.project_type_id === project_type_id
      );
      return projectType && projectType.project_type_code === 'ROOF';
    },
    async openCalcParamsDialog(project) {
      if (!project || !project.bim_project_id) {
        return;
      }

      this.editingCalcProject = project;
      this.calcParamsDialog = true;

      // Загружаем параметры (новая упрощенная функция)
      await this.loadCalcParams(project.bim_project_id);
    },
    async loadProjectTypes() {
      if (this.isLoadingProjectTypes) return;
      this.isLoadingProjectTypes = true;
      try {
        const res = await getProjectTypes();
        this.projectTypesOptions = res || [];
      } catch (e) {
        console.error('Ошибка загрузки типов проектов:', e);
      } finally {
        this.isLoadingProjectTypes = false;
      }
    },
    async loadCalcParams(bim_project_id) {
      this.isLoadingCalcTypes = true;
      try {
        // Используем новую упрощенную функцию
        const params = await getBimProjectCalcParams(bim_project_id);
        
        if (params && params.length > 0) {
          this.calcTypes = params;
          
          // Заполняем форму значениями
          const calcValues = {};
          params.forEach(param => {
            calcValues[param.bim_project_calc_type_id] = param.value || '';
          });
          this.calcParamsForm = calcValues;
        } else {
          this.calcTypes = [];
          this.calcParamsForm = {};
        }
      } catch (e) {
        console.error('Ошибка загрузки параметров расчета:', e);
        this.showErrorSnackbar('Ошибка загрузки параметров расчета');
        this.calcTypes = [];
        this.calcParamsForm = {};
      } finally {
        this.isLoadingCalcTypes = false;
      }
    },
    async saveProject() {
      if (!this.projectForm.project_name) {
        return;
      }

      if (!this.projectForm.project_type_id) {
        this.showErrorSnackbar('Выберите тип проекта');
        return;
      }

      this.isSaving = true;
      const data = {
        bim_project_id: this.editingProject?.bim_project_id || null,
        project_name: this.projectForm.project_name,
        comments: this.projectForm.comments || null,
        project_type_id: this.projectForm.project_type_id || null,
        revit_type: this.projectForm.revit_type || 1,
        resident_id: this.projectForm.resident_id ?? null,
        block_num: this.projectForm.block_num ?? null
      };

      const res = await saveBimProject(data);
      this.isSaving = false;

      if (res) {
        this.showSucceedSnackbar(this.editingProject ? 'Проект обновлен' : 'Проект создан');
        this.projectDialog = false;

        await this.loadProjectsSilently();
      }
    },
    async saveCalcParams() {
      if (!this.editingCalcProject || !this.editingCalcProject.bim_project_id) {
        return;
      }

      if (this.calcTypes.length === 0) {
        this.showErrorSnackbar('Нет параметров для сохранения');
        return;
      }

      this.isSavingCalcParams = true;
      
      try {
        // Формируем массив параметров для сохранения
        const params = this.calcTypes.map(param => {
          const value = this.calcParamsForm[param.bim_project_calc_type_id];
          return {
            bim_project_calc_type_id: param.bim_project_calc_type_id,
            value: value === '' || value === null || value === undefined ? 0 : parseFloat(value) || 0
          };
        });

        // Используем новую упрощенную функцию сохранения
        await saveBimProjectCalcParams(this.editingCalcProject.bim_project_id, params);
        
        this.showSucceedSnackbar('Параметры расчета сохранены');
        this.calcParamsDialog = false;
      } catch (e) {
        console.error('Ошибка сохранения параметров расчета:', e);
        this.showErrorSnackbar('Ошибка сохранения параметров расчета');
      } finally {
        this.isSavingCalcParams = false;
      }
    },
    confirmDeleteProject(project) {
      if (!project || !project.bim_project_id) return;
      this.confirmDialog = {
        show: true,
        title: 'Удаление проекта',
        message: `Удалить проект «${project.project_name || project.bim_project_id}»?`,
        onConfirm: async () => {
          await this.deleteProject(project);
        }
      };
    },
    async deleteProject(project) {
      if (!project || !project.bim_project_id) return;
      try {
        await deleteBimProject(project.bim_project_id);
        this.showSucceedSnackbar('Проект удалён');
        if (this.selectedProject?.bim_project_id === project.bim_project_id) {
          this.selectedProject = null;
          this.items = [];
          this.floorSchemes = [];
        }
        await this.loadProjectsSilently();
      } catch (e) {
        this.showErrorSnackbar(e?.response?.data?.message || 'Ошибка удаления проекта');
      }
    },
    async openItemDialog(item = null) {
      // Загружаем справочники если еще не загружены
      if (this.bimTypes.length === 0) {
        this.isLoadingTypes = true;
        const typesRes = await getBimTypes();
        this.isLoadingTypes = false;
        if (typesRes) {
          this.bimTypes = typesRes;
        }
      }

      if (this.bimParams.length === 0) {
        this.isLoadingParams = true;
        const paramsRes = await getBimParams();
        this.isLoadingParams = false;
        if (paramsRes) {
          this.bimParams = paramsRes;
        }
      }

      this.editingItem = item;
      if (item) {
        this.itemForm = {
          bim_level_num: item.bim_level_num,
          bim_type_id: item.bim_type_id,
          bim_volume: item.bim_volume,
          bim_cnt: item.bim_cnt,
          bim_area: item.bim_area,
          bim_length: item.bim_length,
          bim_diameter: item.bim_diameter,
          bim_weight: item.bim_weight,
          bim_empty_cnt: item.bim_empty_cnt
        };
      } else {
        this.itemForm = {
          bim_level_num: null,
          bim_type_id: null,
          bim_volume: null,
          bim_cnt: null,
          bim_area: null,
          bim_length: null,
          bim_diameter: null,
          bim_weight: null,
          bim_empty_cnt: null
        };
      }
      this.itemDialog = true;
    },
    async saveItem() {
      this.isSavingItem = true;
      const data = {
        bim_project_item_id: this.editingItem?.bim_project_item_id || null,
        bim_project_id: this.selectedProject.bim_project_id,
        bim_type_id: this.itemForm.bim_type_id || null,
        bim_level_num: this.itemForm.bim_level_num || null,
        bim_volume: this.itemForm.bim_volume || null,
        bim_cnt: this.itemForm.bim_cnt || null,
        bim_area: this.itemForm.bim_area || null,
        bim_length: this.itemForm.bim_length || null,
        bim_diameter: this.itemForm.bim_diameter || null,
        bim_weight: this.itemForm.bim_weight || null,
        bim_empty_cnt: this.itemForm.bim_empty_cnt || null
      };

      const res = await saveBimProjectItem(data);
      this.isSavingItem = false;

      if (res) {
        this.showSucceedSnackbar(this.editingItem ? 'Позиция обновлена' : 'Позиция создана');
        this.itemDialog = false;

        await this.loadCategories();
        await this.loadItemsSilently(this.selectedProject.bim_project_id);
      }
    },
    confirmDeleteSelectedItems() {
      this.confirmDialog = {
        show: true,
        title: 'Подтверждение',
        message: `Вы уверены, что хотите удалить ${this.selectedItems.length} позиций?`,
        onConfirm: async () => {
          await this.deleteSelectedItems();
        }
      };
    },
    async deleteSelectedItems() {
      const itemsToDelete = [...this.selectedItems];

      const res = await deleteBimProjectItems(itemsToDelete);

      if (res) {
        this.selectedItems = [];
        this.showSucceedSnackbar(`Удалено ${itemsToDelete.length} позиций`);

        await this.loadItemsSilently(this.selectedProject.bim_project_id);
      }
    },
    addCopyToFloorsInput() {
      this.copyToFloorsInputs.push('');
    },
    removeCopyToFloorsInput(index) {
      if (this.copyToFloorsInputs.length <= 1) return;
      this.copyToFloorsInputs.splice(index, 1);
    },
    openCopyToFloorsDialog() {
      this.copyToFloorsInputs = [''];
      this.copyToFloorsDialog = true;
    },
    openChangeLevelDialog() {
      this.changeLevelValue = '';
      this.changeLevelDialog = true;
    },
    async submitChangeLevel() {
      const levelVal = (this.changeLevelValue || '').trim();
      const selectedIds = [...this.selectedItems];
      if (!selectedIds.length) {
        this.showErrorSnackbar('Выберите позиции для изменения этажа');
        return;
      }
      this.isUpdatingLevel = true;
      try {
        const res = await updateBimProjectItemsLevel(selectedIds, levelVal || null);
        const count = res?.data?.updated_count ?? res?.updated_count ?? 0;
        this.showSucceedSnackbar(res?.message || `Обновлено позиций: ${count}`);
        this.changeLevelDialog = false;
        this.selectedItems = [];
        await this.loadItems(this.selectedProject.bim_project_id);
      } catch (e) {
        this.showErrorSnackbar(e?.response?.data?.message || 'Ошибка обновления этажа');
      } finally {
        this.isUpdatingLevel = false;
      }
    },
    async copySelectedItemsToFloors() {
      const floors = this.copyToFloorsInputs
        .map((s) => (s || '').trim())
        .filter(Boolean);
      if (!floors.length) {
        this.showErrorSnackbar('Укажите хотя бы один этаж');
        return;
      }
      const selectedIds = [...this.selectedItems];
      if (!selectedIds.length) {
        this.showErrorSnackbar('Выберите позиции для копирования');
        return;
      }
      this.isCopyingToFloors = true;
      try {
        const res = await copyBimProjectItemsToFloors(selectedIds, floors);
        const count = res?.data?.created_count ?? res?.created_count ?? 0;
        this.showSucceedSnackbar(res?.message || `Создано копий: ${count}`);
        this.copyToFloorsDialog = false;
        this.selectedItems = [];
        await this.loadItems(this.selectedProject.bim_project_id);
      } catch (e) {
        this.showErrorSnackbar(e?.response?.data?.message || 'Ошибка копирования');
      } finally {
        this.isCopyingToFloors = false;
      }
    },
    async loadFloorSchemeTypesForCategory(bim_category_id) {
      this.isLoadingFloorSchemeTypes = true;
      try {
        if (bim_category_id) {
          const data = await getBimTypesByCategory(bim_category_id);
          this.floorSchemeTypesOptions = data?.data ?? data ?? [];
        } else {
          const data = await getBimTypes();
          this.floorSchemeTypesOptions = Array.isArray(data) ? data : (data?.data ?? []);
        }
      } catch (e) {
        this.floorSchemeTypesOptions = [];
      } finally {
        this.isLoadingFloorSchemeTypes = false;
      }
    },
    async loadFloorSchemeItemsForCategory(bim_category_id) {
      if (!this.selectedProject?.bim_project_id) {
        this.floorSchemeItemsOptions = [];
        return;
      }
      this.isLoadingFloorSchemeItems = true;
      try {
        const list = await getBimProjectItems(
          this.selectedProject.bim_project_id,
          bim_category_id ?? undefined
        );
        const items = list ?? [];
        this.floorSchemeItemsOptions = items.map((it) => ({
          ...it,
          item_label: it.type_name ? `${it.type_name} (эт. ${it.bim_level_num ?? '-'})` : `#${it.bim_project_item_id}`
        }));
      } catch (e) {
        this.floorSchemeItemsOptions = [];
      } finally {
        this.isLoadingFloorSchemeItems = false;
      }
    },
    getFloorSchemeParamLabel(paramKey) {
      const o = this.floorSchemeParamOptions.find((p) => p.key === paramKey);
      return o ? o.label : 'Значение';
    },
    onFloorSchemeItemChange(bim_project_item_id) {
      this.floorSchemeForm.bim_project_item_id = bim_project_item_id;
      const item = this.floorSchemeItemsOptions.find((it) => it.bim_project_item_id === bim_project_item_id);
      this.selectedFloorSchemeItem = item || null;
    },
    handleFloorSchemeFileChange(file) {
      this.floorSchemeForm.file = file || null;
      this.floorSchemeForm.file_name = file?.name ?? '';
      this.floorSchemeForm.mime_type = file?.type ?? '';
    },
    clearFloorSchemeFile() {
      this.floorSchemeForm.file = null;
      this.floorSchemeForm.file_name = '';
      this.floorSchemeForm.mime_type = '';
    },
    openFloorSchemeDialog(floorScheme = null) {
      this.editingFloorScheme = floorScheme;
      this.floorSchemeForm = {
        bim_level_num: null,
        file_path: '',
        file_name: '',
        mime_type: '',
        file: null
      };
      if (floorScheme) {
        if (floorScheme.bim_level_num !== undefined) this.floorSchemeForm.bim_level_num = floorScheme.bim_level_num;
        if (floorScheme.file_path) this.floorSchemeForm.file_path = floorScheme.file_path;
        if (floorScheme.file_name) this.floorSchemeForm.file_name = floorScheme.file_name;
        if (floorScheme.mime_type) this.floorSchemeForm.mime_type = floorScheme.mime_type;
      }
      this.floorSchemeDialog = true;
    },
    async saveFloorScheme() {
      if (!this.selectedProject?.bim_project_id) return;
      if (this.floorSchemeForm.bim_level_num === null || this.floorSchemeForm.bim_level_num === '') {
        this.showErrorSnackbar('Укажите этаж');
        return;
      }
      const hasFile = this.floorSchemeForm.file && this.floorSchemeForm.file instanceof File;
      const hasPathInfo = this.floorSchemeForm.file_path || this.floorSchemeForm.file_name;
      this.isSavingFloorScheme = true;
      try {
        const formData = new FormData();
        formData.append('bim_project_id', this.selectedProject.bim_project_id);
        formData.append('bim_level_num', this.floorSchemeForm.bim_level_num ?? '');
        if (this.editingFloorScheme?.bim_project_floor_scheme_id) {
          formData.append('bim_project_floor_scheme_id', this.editingFloorScheme.bim_project_floor_scheme_id);
        }
        if (hasFile) {
          formData.append('file', this.floorSchemeForm.file);
        } else if (this.floorSchemeForm.file_path || this.floorSchemeForm.file_name) {
          if (this.floorSchemeForm.file_path) formData.append('file_path', this.floorSchemeForm.file_path);
          if (this.floorSchemeForm.file_name) formData.append('file_name', this.floorSchemeForm.file_name);
          if (this.floorSchemeForm.mime_type) formData.append('mime_type', this.floorSchemeForm.mime_type);
        }
        await saveBimProjectFloorScheme(formData);
        this.showSucceedSnackbar(this.editingFloorScheme ? 'Схема этажа обновлена' : 'Схема этажа создана');
        this.floorSchemeDialog = false;
        await this.loadFloorSchemes(this.selectedProject.bim_project_id);
      } catch (e) {
        this.showErrorSnackbar(e?.response?.data?.message || 'Ошибка сохранения');
      } finally {
        this.isSavingFloorScheme = false;
      }
    },
    async onFloorValueCategoryChange(bim_category_id) {
      this.floorValueForm.bim_category_id = bim_category_id;
      this.floorValueForm.bim_type_id = null;
      await this.loadFloorSchemeTypesForCategory(bim_category_id);
    },
    addFloorValueParamRow() {
      this.floorValueForm.paramRows.push({ param_key: null, param_value: null });
    },
    removeFloorValueParamRow(index) {
      if (this.floorValueForm.paramRows.length <= 1) return;
      this.floorValueForm.paramRows.splice(index, 1);
    },
    async openFloorValueDialog() {
      this.floorValueForm = {
        bim_category_id: null,
        bim_level_num: null,
        bim_type_id: null,
        paramRows: [{ param_key: null, param_value: null }]
      };
      this.floorSchemeTypesOptions = [];
      const res = await getBimCategories();
      this.allCategoriesList = res || [];
      this.floorValueDialog = true;
    },
    async saveFloorValue() {
      if (!this.selectedProject?.bim_project_id) return;
      if (this.floorValueForm.bim_level_num === null || this.floorValueForm.bim_level_num === '') {
        this.showErrorSnackbar('Укажите этаж');
        return;
      }
      if (!this.floorValueForm.bim_type_id) {
        this.showErrorSnackbar('Выберите тип');
        return;
      }
      const filledRows = this.floorValueForm.paramRows.filter(
        (r) => r.param_key && (r.param_value !== null && r.param_value !== '')
      );
      if (!filledRows.length) {
        this.showErrorSnackbar('Укажите хотя бы один параметр и значение');
        return;
      }
      this.isSavingFloorValue = true;
      try {
        const payload = {
          bim_project_item_id: null,
          bim_project_id: this.selectedProject.bim_project_id,
          bim_type_id: this.floorValueForm.bim_type_id,
          bim_level_num: this.floorValueForm.bim_level_num,
          bim_volume: null,
          bim_cnt: null,
          bim_area: null,
          bim_length: null,
          bim_diameter: null,
          bim_weight: null,
          bim_empty_cnt: null
        };
        filledRows.forEach((row) => {
          if (payload.hasOwnProperty(row.param_key)) {
            payload[row.param_key] = row.param_value;
          }
        });
        await saveBimProjectItem(payload);
        this.showSucceedSnackbar('Запись создана');
        this.floorValueDialog = false;
        await this.loadCategories();
        await this.loadItems(this.selectedProject.bim_project_id);
      } catch (e) {
        this.showErrorSnackbar(e?.response?.data?.message || 'Ошибка сохранения');
      } finally {
        this.isSavingFloorValue = false;
      }
    },
    confirmDeleteSelectedFloorSchemes() {
      this.confirmDialog = {
        show: true,
        title: 'Подтверждение',
        message: `Вы уверены, что хотите удалить ${this.selectedFloorSchemes.length} схем этажей?`,
        onConfirm: async () => {
          await this.deleteSelectedFloorSchemes();
        }
      };
    },
    async deleteSelectedFloorSchemes() {
      this.deletionProgress = {
        current: 0,
        total: this.selectedFloorSchemes.length
      };
      this.deletionProgressDialog = true;

      const schemesToDelete = [...this.selectedFloorSchemes];

      for (const schemeId of schemesToDelete) {
        await deleteBimProjectFloorScheme(schemeId);
        this.deletionProgress.current++;
      }

      this.deletionProgressDialog = false;
      this.selectedFloorSchemes = [];
      this.showSucceedSnackbar(`Удалено ${schemesToDelete.length} схем этажей`);

      await this.loadFloorSchemesSilently(this.selectedProject.bim_project_id);
    },
    async onConfirmDelete() {
      this.confirmDialog.show = false;
      if (this.confirmDialog.onConfirm) {
        await this.confirmDialog.onConfirm();
      }
    },
    formatNumber(value) {
      if (value === null || value === undefined) return '-';
      const num = parseFloat(value);
      if (isNaN(num)) return '-';
      return num.toFixed(3);
    },
    getImageUrl(filePath) {
      if (!filePath) return '';
      // Если путь уже полный URL, возвращаем как есть
      if (filePath.startsWith('http')) return filePath;
      // Иначе добавляем базовый URL API
      const API_URL = process.env.VUE_APP_API_URL || 'http://localhost:8000';
      return `${API_URL}${filePath}`;
    },
    isImageMimeType(mimeType) {
      if (!mimeType) return false;
      return mimeType.startsWith('image/');
    },
    openImagePreview(filePath) {
      this.imagePreviewUrl = this.getImageUrl(filePath);
      this.imagePreviewDialog = true;
    },
    openCropDialog(floorScheme) {
      this.cropCurrentScheme = floorScheme;
      this.cropImageUrl = this.getImageUrl(floorScheme.file_path);
      this.cropDialog = true;

      // Сбрасываем координаты обрезки
      this.cropStartX = 0;
      this.cropStartY = 0;
      this.cropEndX = 0;
      this.cropEndY = 0;
      this.isCropping = false;

      // Загружаем изображение
      this.$nextTick(() => {
        this.initCropCanvas();
      });
    },
    openCopyFloorSchemeDialog(row) {
      this.copyFloorSchemeSource = row;
      this.copyFloorSchemeTargetIds = [];
      this.copyFloorSchemeDialog = true;
    },
    toggleCopyFloorSchemeTarget(schemeId, checked) {
      if (checked) {
        if (!this.copyFloorSchemeTargetIds.includes(schemeId)) {
          this.copyFloorSchemeTargetIds = [...this.copyFloorSchemeTargetIds, schemeId];
        }
      } else {
        this.copyFloorSchemeTargetIds = this.copyFloorSchemeTargetIds.filter((id) => id !== schemeId);
      }
    },
    async copyFloorSchemeToFloorsSubmit() {
      if (!this.copyFloorSchemeSource || !this.copyFloorSchemeTargetIds.length) return;
      this.isCopyingFloorScheme = true;
      try {
        await copyBimProjectFloorSchemeToFloors({
          source_bim_project_floor_scheme_id: this.copyFloorSchemeSource.bim_project_floor_scheme_id,
          target_bim_project_floor_scheme_ids: this.copyFloorSchemeTargetIds
        });
        this.showSucceedSnackbar('Схема успешно скопирована на выбранные этажи');
        this.copyFloorSchemeDialog = false;
        this.copyFloorSchemeSource = null;
        this.copyFloorSchemeTargetIds = [];
        await this.loadFloorSchemes(this.selectedProject?.bim_project_id);
      } catch (e) {
        this.showErrorSnackbar(e?.response?.data?.message || e?.message || 'Ошибка копирования');
      } finally {
        this.isCopyingFloorScheme = false;
      }
    },
    initCropCanvas() {
      const canvas = this.$refs.cropCanvas;
      if (!canvas) return;

      const img = new Image();
      img.src = this.cropImageUrl;
      img.onload = () => {
        this.cropImage = img;

        // Устанавливаем размеры canvas под изображение
        const maxWidth = window.innerWidth - 100;
        const maxHeight = window.innerHeight - 250;

        let canvasWidth = img.width;
        let canvasHeight = img.height;

        // Масштабируем если изображение слишком большое
        if (canvasWidth > maxWidth || canvasHeight > maxHeight) {
          const scale = Math.min(maxWidth / canvasWidth, maxHeight / canvasHeight);
          canvasWidth = canvasWidth * scale;
          canvasHeight = canvasHeight * scale;
        }

        canvas.width = canvasWidth;
        canvas.height = canvasHeight;

        this.cropCtx = canvas.getContext('2d');
        this.drawCropCanvas();
      };
      img.onerror = () => {
        this.showErrorSnackbar('Ошибка загрузки изображения');
      };
    },
    drawCropCanvas() {
      if (!this.cropCtx || !this.cropImage) return;

      const canvas = this.$refs.cropCanvas;
      const ctx = this.cropCtx;

      // Очищаем canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Рисуем изображение
      ctx.drawImage(this.cropImage, 0, 0, canvas.width, canvas.height);

      // Если есть выделение, рисуем его
      if (this.isCropping || (this.cropStartX !== 0 || this.cropEndX !== 0)) {
        const x = Math.min(this.cropStartX, this.cropEndX);
        const y = Math.min(this.cropStartY, this.cropEndY);
        const width = Math.abs(this.cropEndX - this.cropStartX);
        const height = Math.abs(this.cropEndY - this.cropStartY);

        // Затемняем область вне выделения
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(0, 0, canvas.width, y); // Верх
        ctx.fillRect(0, y, x, height); // Лево
        ctx.fillRect(x + width, y, canvas.width - x - width, height); // Право
        ctx.fillRect(0, y + height, canvas.width, canvas.height - y - height); // Низ

        // Рисуем рамку выделения
        ctx.strokeStyle = '#1976d2';
        ctx.lineWidth = 2;
        ctx.strokeRect(x, y, width, height);

        // Рисуем углы выделения
        const cornerSize = 10;
        ctx.fillStyle = '#1976d2';
        // Верхний левый
        ctx.fillRect(x - cornerSize/2, y - cornerSize/2, cornerSize, cornerSize);
        // Верхний правый
        ctx.fillRect(x + width - cornerSize/2, y - cornerSize/2, cornerSize, cornerSize);
        // Нижний левый
        ctx.fillRect(x - cornerSize/2, y + height - cornerSize/2, cornerSize, cornerSize);
        // Нижний правый
        ctx.fillRect(x + width - cornerSize/2, y + height - cornerSize/2, cornerSize, cornerSize);
      }
    },
    startCrop(event) {
      const canvas = this.$refs.cropCanvas;
      const rect = canvas.getBoundingClientRect();
      this.cropStartX = event.clientX - rect.left;
      this.cropStartY = event.clientY - rect.top;
      this.cropEndX = this.cropStartX;
      this.cropEndY = this.cropStartY;
      this.isCropping = true;
    },
    updateCrop(event) {
      if (!this.isCropping) return;

      const canvas = this.$refs.cropCanvas;
      const rect = canvas.getBoundingClientRect();
      this.cropEndX = event.clientX - rect.left;
      this.cropEndY = event.clientY - rect.top;

      this.drawCropCanvas();
    },
    endCrop(event) {
      if (!this.isCropping) return;

      this.isCropping = false;
      const canvas = this.$refs.cropCanvas;
      const rect = canvas.getBoundingClientRect();
      this.cropEndX = event.clientX - rect.left;
      this.cropEndY = event.clientY - rect.top;

      this.drawCropCanvas();
    },
    async saveCroppedImage() {
      if (!this.cropImage || !this.cropCurrentScheme) {
        this.showErrorSnackbar('Ошибка: изображение не загружено');
        return;
      }

      if (!this.selectedProject || !this.selectedProject.bim_project_id) {
        this.showErrorSnackbar('Ошибка: проект не выбран');
        return;
      }

      // Сохраняем ID проекта в переменную
      const projectId = this.selectedProject.bim_project_id;
      const floorSchemeId = this.cropCurrentScheme.bim_project_floor_scheme_id;
      const levelNum = this.cropCurrentScheme.bim_level_num;
      const fileName = this.cropCurrentScheme.file_name || 'cropped_image.png';

      // Вычисляем координаты обрезки
      const canvas = this.$refs.cropCanvas;
      const x = Math.min(this.cropStartX, this.cropEndX);
      const y = Math.min(this.cropStartY, this.cropEndY);
      const width = Math.abs(this.cropEndX - this.cropStartX);
      const height = Math.abs(this.cropEndY - this.cropStartY);

      if (width < 10 || height < 10) {
        this.showErrorSnackbar('Выберите область для обрезки');
        return;
      }

      // Вычисляем масштаб
      const scaleX = this.cropImage.width / canvas.width;
      const scaleY = this.cropImage.height / canvas.height;

      // Координаты на оригинальном изображении
      const origX = x * scaleX;
      const origY = y * scaleY;
      const origWidth = width * scaleX;
      const origHeight = height * scaleY;

      // Создаем новый canvas для обрезки
      const cropCanvas = document.createElement('canvas');
      cropCanvas.width = origWidth;
      cropCanvas.height = origHeight;
      const cropCtx = cropCanvas.getContext('2d');

      // Копируем выделенную область
      cropCtx.drawImage(
        this.cropImage,
        origX, origY, origWidth, origHeight,
        0, 0, origWidth, origHeight
      );

      // Конвертируем в blob
      cropCanvas.toBlob(async (blob) => {
        if (!blob) {
          this.showErrorSnackbar('Ошибка создания изображения');
          return;
        }

        // Создаем файл из blob
        const file = new File([blob], fileName, { type: 'image/png' });

        // Создаем FormData для отправки
        const formData = new FormData();
        formData.append('bim_project_floor_scheme_id', parseInt(floorSchemeId));
        formData.append('bim_project_id', parseInt(projectId));
        formData.append('bim_level_num', levelNum);
        formData.append('file', file);

        // Отправляем на сервер
        this.isSavingFloorScheme = true;
        const res = await saveBimProjectFloorScheme(formData);
        this.isSavingFloorScheme = false;

        if (res) {
          this.showSucceedSnackbar('Изображение обрезано и сохранено');
          this.cropDialog = false;

          // Обновляем список схем
          await this.loadFloorSchemesSilently(projectId);
        } else {
          this.showErrorSnackbar('Ошибка сохранения изображения');
        }
      }, 'image/png');
    },
    openImportForProject(project) {
      if (!project) return;
      this.selectProject(project);
      this.$nextTick(() => this.openImportJsonDialog());
    },
    openImportExcelForProject(project) {
      if (!project) return;
      this.selectProject(project);
      this.$nextTick(() => this.openImportExcelDialog());
    },
    async openImportToDigitalProject(project) {
      if (!project?.bim_project_id) return;
      try {
        await instance().post('/project_list/project/load_revit/', {
          bim_project_id: project.bim_project_id
        });
        this.showSucceedSnackbar('Импорт в цифровой проект выполнен');
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Ошибка при импорте в цифровой проект';
        this.showErrorSnackbar(msg);
      }
    },
    async openImportJsonDialog(project = null) {
      if (project) {
        this.selectProject(project);
      }
      if (!this.selectedProject) {
        this.showErrorSnackbar('Выберите проект для импорта');
        return;
      }
      this.importFileGroups = [
        {
          type: 'other',
          files: []
        }
      ];
      this.importResultStats = [];
      this.importResultMessage = '';
      this.isDeletePrev = true;
      this.projectCategories = [];
      this.importJsonDialog = true;
      
      this.loadProjectCategories();
    },
    openImportExcelDialog(project = null) {
      if (project) {
        this.selectProject(project);
      }
      if (!this.selectedProject) {
        this.showErrorSnackbar('Выберите проект для импорта');
        return;
      }
      this.excelImportFile = null;
      this.importExcelResultMessage = '';
      this.importExcelResultStats = [];
      this.isDeletePrev = true;
      this.projectCategories = [];
      this.importExcelDialog = true;
      this.loadProjectCategories();
    },
    async loadProjectCategories() {
      if (!this.selectedProject) return;
      
      this.isLoadingProjectCategories = true;
      try {
        const categories = await getBimCategories(this.selectedProject.bim_project_id);
        this.projectCategories = categories || [];
      } catch (e) {
        console.error('Ошибка загрузки категорий проекта:', e);
        this.projectCategories = [];
      } finally {
        this.isLoadingProjectCategories = false;
      }
    },
    buildImportFormData(allow_remove_manual = undefined) {
      const formData = new FormData();
      formData.append('is_delete_prev', this.isDeletePrev);
      if (allow_remove_manual !== undefined) {
        formData.append('allow_remove_manual', allow_remove_manual ? 'true' : 'false');
      }
      this.importFileGroups.forEach((group) => {
        if (!group.files || group.files.length === 0) return;
        const fieldName = 'files_other';
        group.files.forEach((file) => {
          formData.append(fieldName, file);
        });
      });
      return formData;
    },
    buildImportExcelFormData() {
      const formData = new FormData();
      if (this.excelImportFile) {
        formData.append('excel_file', this.excelImportFile);
      }
      return formData;
    },
    async doImportJson(allow_remove_manual = undefined) {
      const formData = this.buildImportFormData(allow_remove_manual);
      const res = await importBimProjectJson(this.selectedProject.bim_project_id, formData);
      if (res) {
        this.importResultMessage = res.message || 'Данные успешно импортированы';
        this.importResultStats = Array.isArray(res.stats) ? res.stats : [];
        if (res.warnings?.length) {
          res.warnings.forEach(warning => this.showWarningSnackbar(warning));
        } else {
          this.showSucceedSnackbar(this.importResultMessage);
        }
        await Promise.all([
          this.loadProjectCategories(),
          this.loadItemsSilently(this.selectedProject.bim_project_id),
          this.loadFloorSchemesSilently(this.selectedProject.bim_project_id)
        ]);
      }
      return res;
    },
    async doImportExcel() {
      const formData = this.buildImportExcelFormData();
      const res = await importBimProjectExcel(
        this.selectedProject.bim_project_id,
        formData
      );
      if (res) {
        this.importExcelResultMessage = res.message || 'Данные успешно импортированы';
        this.importExcelResultStats = Array.isArray(res.stats) ? res.stats : [];
        if (res.warnings?.length) {
          res.warnings.forEach((warning) => this.showWarningSnackbar(warning));
        } else {
          this.showSucceedSnackbar(this.importExcelResultMessage);
        }
        await Promise.all([
          this.loadProjectCategories(),
          this.loadItemsSilently(this.selectedProject.bim_project_id),
          this.loadFloorSchemesSilently(this.selectedProject.bim_project_id)
        ]);
      }
      return res;
    },
    async importJson() {
      this.manualConflictImportKind = 'json';
      if (!this.hasImportFiles) {
        this.showErrorSnackbar('Добавьте хотя бы один набор файлов JSON или ZIP');
        return;
      }

      this.isImportingJson = true;

      try {
        await this.doImportJson();
      } catch (error) {
        const data = error?.response?.data || {};
        const errMsg = (data.error || data.message || data.detail || error?.message || '').toString();
        const isManualConflict =
          data.is_manual === true ||
          errMsg.includes('ручным вводом') ||
          errMsg.includes('удалить или сохранить');

        if (isManualConflict) {
          this.showManualConflictDialog = true;
        } else {
          this.showErrorSnackbar(errMsg || 'Ошибка при импорте JSON');
        }
      } finally {
        this.isImportingJson = false;
      }
    },
    onManualConflictChoice(allow_remove_manual) {
      this.showManualConflictDialog = false;
      if (this.manualConflictImportKind === 'excel') {
        this.runImportExcelWithChoice(allow_remove_manual);
      } else {
        this.runImportWithChoice(allow_remove_manual);
      }
    },
    async runImportWithChoice(allow_remove_manual) {
      this.isImportingJson = true;
      try {
        await this.doImportJson(allow_remove_manual);
      } catch (error) {
        const data = error?.response?.data || {};
        const errMsg = (data.error || data.message || error?.message || '').toString();
        this.showErrorSnackbar(errMsg || 'Ошибка при импорте JSON');
      } finally {
        this.isImportingJson = false;
      }
    },
    async importProjectExcel() {
      if (!this.excelImportFile) {
        this.showErrorSnackbar('Выберите файл Excel или CSV');
        return;
      }
      this.isImportingExcel = true;
      try {
        await this.doImportExcel();
      } catch (error) {
        const data = error?.response?.data || {};
        const errMsg = (
          data.error || data.message || data.detail || error?.message || ''
        ).toString();
        this.showErrorSnackbar(errMsg || 'Ошибка при импорте Excel');
      } finally {
        this.isImportingExcel = false;
      }
    },
    onFileRejected(rejectedEntries) {
      const rejection = rejectedEntries[0];
      if (rejection.failedPropValidation === 'max-file-size') {
        this.showErrorSnackbar('Файл слишком большой. Максимальный размер: 50 МБ');
      } else {
        this.showErrorSnackbar('Файл не соответствует требованиям');
      }
    },
    addImportGroup() {
      this.importFileGroups.push({
        type: 'other',
        files: []
      });
    },
    removeImportGroup(index) {
      this.importFileGroups.splice(index, 1);
    },
    clearGroupFiles(index) {
      this.importFileGroups[index].files = [];
    },
    removeFileFromGroup(groupIndex, fileIndex) {
      this.importFileGroups[groupIndex].files.splice(fileIndex, 1);
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },
    formatDate(dateString) {
      if (!dateString) return '';

      // Если уже в формате DD.MM.YYYY — возвращаем как есть
      if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateString)) return dateString;

      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}.${month}.${year} ${hours}:${minutes}`;
    },
    showErrorSnackbar(message) {
      this.$q.notify({
        type: 'negative',
        message: message,
        position: 'top',
        timeout: 3000
      });
    },
    async openGeometryDialog(floorScheme) {
      if (!this.selectedProject || !floorScheme.bim_level_num) {
        this.showErrorSnackbar('Не удалось загрузить геометрию');
        return;
      }

      this.currentFloorLevel = floorScheme.bim_level_num;
      this.currentFloorSchemeId = floorScheme.bim_project_floor_scheme_id;
      this.geometryDialog = true;
      this.isLoadingGeometry = true;
      this.geometryData = [];
      this.canvasZoom = 1;
      this.canvasOffsetX = 0;
      this.canvasOffsetY = 0;
      this.floorImage = null;
      this.calibrationMode = false;
      this.coordinatesMode = false;
      this.typeSearchQuery = '';

      // Загружаем калибровку
      this.imageOffsetX = floorScheme.image_offset_x || 0;
      this.imageOffsetY = floorScheme.image_offset_y || 0;
      this.imageWidth = floorScheme.image_width || 0;
      this.imageHeight = floorScheme.image_height || 0;

      // Загружаем изображение обложки если есть
      if (floorScheme.file_path) {
        this.isLoadingFloorImage = true;
        const img = new Image();

        img.onload = () => {
          this.floorImage = img;
          // Если нет сохраненной калибровки, устанавливаем размеры по умолчанию
          if (!this.imageWidth || !this.imageHeight) {
            // Если геометрия уже загружена, используем её bounds
            if (this.geometryBounds) {
              const geomWidth = this.geometryBounds.maxX - this.geometryBounds.minX;
              const geomHeight = this.geometryBounds.maxY - this.geometryBounds.minY;

              // Устанавливаем размер изображения равным размеру геометрии
              const aspectRatio = img.width / img.height;
              const geomAspectRatio = geomWidth / geomHeight;

              if (aspectRatio > geomAspectRatio) {
                // Изображение шире - подгоняем по ширине
                this.imageWidth = geomWidth;
                this.imageHeight = geomWidth / aspectRatio;
              } else {
                // Изображение выше - подгоняем по высоте
                this.imageHeight = geomHeight;
                this.imageWidth = geomHeight * aspectRatio;
              }

              // Начальная позиция - левый нижний угол геометрии
              this.imageOffsetX = this.geometryBounds.minX;
              this.imageOffsetY = this.geometryBounds.minY;
            } else {
              // Если геометрии еще нет, используем временные значения
              // которые обновятся после загрузки геометрии
              this.imageWidth = img.width;
              this.imageHeight = img.height;
              this.imageOffsetX = 0;
              this.imageOffsetY = 0;
            }
          }
          this.isLoadingFloorImage = false;
          this.drawGeometry();
        };

        img.onerror = () => {
          this.floorImage = null;
          this.isLoadingFloorImage = false;
        };

        img.src = this.getImageUrl(floorScheme.file_path);
      }

      try {
        const res = await getBimProjectFloorSchemeGeometry(
          this.selectedProject.bim_project_id,
          floorScheme.bim_level_num
        );

        if (res && res.length > 0) {
          this.geometryData = res;
          // Даем время на отрисовку dialog, затем рисуем canvas
          this.$nextTick(() => {
            setTimeout(() => {
              this.prepareGeometry();
              this.drawGeometry();
            }, 100);
          });
        } else {
          this.geometryData = [];
        }
      } catch (error) {
        console.error('Ошибка загрузки геометрии:', error);
        this.showErrorSnackbar('Ошибка при загрузке геометрии');
      } finally {
        this.isLoadingGeometry = false;
      }
    },
    loadFloorImage(filePath) {
      this.isLoadingFloorImage = true;
      const img = new Image();
      img.src = this.getImageUrl(filePath);

      img.onload = () => {
        this.floorImage = img;
        this.isLoadingFloorImage = false;
        this.drawGeometry();
      };

      img.onerror = () => {
        console.error('Ошибка загрузки изображения обложки');
        this.floorImage = null;
        this.isLoadingFloorImage = false;
      };

    },
    parseMultiLineString(wktString) {
      // Парсим MULTILINESTRING((...),(...))
      if (!wktString || !wktString.startsWith('MULTILINESTRING')) {
        return [];
      }

      try {
        // Убираем "MULTILINESTRING(" и последнюю ")"
        let coordsStr = wktString.substring('MULTILINESTRING('.length);
        coordsStr = coordsStr.substring(0, coordsStr.length - 1);

        // Теперь у нас строка вида: (x1 y1,x2 y2,...),(x3 y3,x4 y4,...),(...)
        // Разбиваем на группы линий
        const lineGroups = [];
        let currentGroup = '';
        let depth = 0;

        for (let i = 0; i < coordsStr.length; i++) {
          const char = coordsStr[i];

          if (char === '(') {
            depth++;
            if (depth === 1) continue;
          } else if (char === ')') {
            depth--;
            if (depth === 0) {
              if (currentGroup.trim()) {
                lineGroups.push(currentGroup.trim());
              }
              currentGroup = '';
              continue;
            }
          } else if (char === ',' && depth === 0) {
            continue;
          }

          if (depth > 0) {
            currentGroup += char;
          }
        }

        const lines = [];

        lineGroups.forEach(group => {
          const points = group.split(',').map(pair => {
            const parts = pair.trim().split(/\s+/);
            const x = parseFloat(parts[0]);
            const y = parseFloat(parts[1]);
            return { x, y };
          }).filter(p => !isNaN(p.x) && !isNaN(p.y));

          if (points.length > 0) {
            lines.push(points);
          }
        });

        return lines;
      } catch (error) {
        console.error('Ошибка парсинга WKT:', error);
        return [];
      }
    },
    prepareGeometry() {
      // Собираем все линии и точки один раз
      let allPoints = [];
      const geometryLines = [];
      const typeSummary = {};

      this.geometryData.forEach(item => {
        const wktString = item.geom || item.st_astext || item.geometry;
        const lines = this.parseMultiLineString(wktString);
        const bimTypeId = String(item.bim_type_id || 'unknown'); // Конвертируем в строку
        const typeName = item.type_name || 'Без типа';
        const volume = parseFloat(item.bim_volume) || 0;

        // Суммируем объем по типам (используем bim_type_id как ключ)
        if (!typeSummary[bimTypeId]) {
          typeSummary[bimTypeId] = {
            typeName: typeName,
            volume: 0,
            count: 0
          };
        }
        typeSummary[bimTypeId].volume += volume;
        typeSummary[bimTypeId].count += 1;

        lines.forEach(line => {
          geometryLines.push({
            points: line,
            bimTypeId: bimTypeId,
            typeName: typeName,
            revitId: item.revit_id,
            bimInfo: item.bim_info,
            volume: volume
          });
          allPoints = allPoints.concat(line);
        });
      });

      if (allPoints.length === 0) {
        return;
      }

      // Находим границы
      const minX = Math.min(...allPoints.map(p => p.x));
      const maxX = Math.max(...allPoints.map(p => p.x));
      const minY = Math.min(...allPoints.map(p => p.y));
      const maxY = Math.max(...allPoints.map(p => p.y));

      this.geometryLines = geometryLines;
      this.geometryBounds = { minX, maxX, minY, maxY };
      this.typeSummary = typeSummary;

      // Всегда инициализируем выбранные типы (все выбраны по умолчанию)
      this.selectedTypes = Object.keys(typeSummary);
      this.allTypesSelected = true;

      // Если изображение загружено, но размеры еще не установлены (загрузилось раньше геометрии)
      if (this.floorImage && (!this.imageWidth || !this.imageHeight ||
          this.imageWidth === this.floorImage.width || this.imageHeight === this.floorImage.height)) {
        const geomWidth = maxX - minX;
        const geomHeight = maxY - minY;

        const aspectRatio = this.floorImage.width / this.floorImage.height;
        const geomAspectRatio = geomWidth / geomHeight;

        if (aspectRatio > geomAspectRatio) {
          this.imageWidth = geomWidth;
          this.imageHeight = geomWidth / aspectRatio;
        } else {
          this.imageHeight = geomHeight;
          this.imageWidth = geomHeight * aspectRatio;
        }

        this.imageOffsetX = minX;
        this.imageOffsetY = minY;
      }
    },
    drawGeometry() {
      const canvas = this.$refs.geometryCanvas;
      const container = this.$refs.canvasContainer;

      if (!canvas || !container || !this.geometryBounds) {
        return;
      }

      const ctx = canvas.getContext('2d');

      // Устанавливаем размер canvas под контейнер
      const width = container.clientWidth || 1200;
      const height = container.clientHeight || 800;
      canvas.width = width;
      canvas.height = height;

      // Очищаем canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#f5f5f5';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const { minX, maxX, minY, maxY } = this.geometryBounds;
      const dataWidth = maxX - minX;
      const dataHeight = maxY - minY;

      // Добавляем отступы
      const padding = 50;
      const availableWidth = canvas.width - 2 * padding;
      const availableHeight = canvas.height - 2 * padding;

      // Базовый масштаб
      let scaleX = availableWidth / dataWidth;
      let scaleY = availableHeight / dataHeight;
      let baseScale = Math.min(scaleX, scaleY);

      // Применяем зум
      const scale = baseScale * this.canvasZoom;

      // Центрируем с учетом смещения
      const scaledWidth = dataWidth * scale;
      const scaledHeight = dataHeight * scale;
      const offsetX = (canvas.width - scaledWidth) / 2 + this.canvasOffsetX;
      const offsetY = (canvas.height - scaledHeight) / 2 + this.canvasOffsetY;

      // Функция преобразования координат
      const transformX = (x) => offsetX + (x - minX) * scale;
      const transformY = (y) => offsetY + (maxY - y) * scale;

      // Сохраняем bounds для использования в других методах
      this.geometryBounds.scale = scale;
      this.geometryBounds.padding = padding;
      this.geometryBounds.offsetX = offsetX;
      this.geometryBounds.offsetY = offsetY;

      // Сначала рисуем линии (только выбранные типы)
      this.geometryLines.forEach((geom, index) => {
        // Пропускаем если тип не выбран
        if (!this.selectedTypes.includes(geom.bimTypeId)) {
          return;
        }

        ctx.beginPath();
        ctx.strokeStyle = this.getColorForIndex(index);
        ctx.lineWidth = 2;

        geom.points.forEach((point, i) => {
          const x = transformX(point.x);
          const y = transformY(point.y);

          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        });

        ctx.stroke();

        // Рисуем точки если зум достаточно большой
        if (this.canvasZoom > 1.5) {
          ctx.fillStyle = this.getColorForIndex(index);
          geom.points.forEach(point => {
            const x = transformX(point.x);
            const y = transformY(point.y);
            ctx.beginPath();
            ctx.arc(x, y, 3, 0, Math.PI * 2);
            ctx.fill();
          });
        }
      });

      // Теперь рисуем обложку ПОВЕРХ линий
      if (this.floorImage && this.imageWidth > 0 && this.imageHeight > 0) {
        ctx.save();
        ctx.globalAlpha = this.floorImageOpacity;

        // Используем сохраненные координаты калибровки
        const imageCanvasX = transformX(this.imageOffsetX);
        const imageCanvasY = transformY(this.imageOffsetY + this.imageHeight);
        const imageCanvasWidth = this.imageWidth * scale;
        const imageCanvasHeight = this.imageHeight * scale;

        ctx.drawImage(this.floorImage, imageCanvasX, imageCanvasY, imageCanvasWidth, imageCanvasHeight);

        // В режиме калибровки отрисовываем рамку и ручки для изменения размера
        if (this.calibrationMode) {
          ctx.strokeStyle = '#ff0000';
          ctx.lineWidth = 2;
          ctx.setLineDash([5, 5]);
          ctx.strokeRect(imageCanvasX, imageCanvasY, imageCanvasWidth, imageCanvasHeight);
          ctx.setLineDash([]);

          // Рисуем ручки для изменения размера (8 точек: 4 угла + 4 стороны)
          const handleSize = 8;
          ctx.fillStyle = '#ff0000';

          const handles = [
            { x: imageCanvasX, y: imageCanvasY, type: 'nw' }, // Верхний левый
            { x: imageCanvasX + imageCanvasWidth / 2, y: imageCanvasY, type: 'n' }, // Верхний средний
            { x: imageCanvasX + imageCanvasWidth, y: imageCanvasY, type: 'ne' }, // Верхний правый
            { x: imageCanvasX + imageCanvasWidth, y: imageCanvasY + imageCanvasHeight / 2, type: 'e' }, // Правый средний
            { x: imageCanvasX + imageCanvasWidth, y: imageCanvasY + imageCanvasHeight, type: 'se' }, // Нижний правый
            { x: imageCanvasX + imageCanvasWidth / 2, y: imageCanvasY + imageCanvasHeight, type: 's' }, // Нижний средний
            { x: imageCanvasX, y: imageCanvasY + imageCanvasHeight, type: 'sw' }, // Нижний левый
            { x: imageCanvasX, y: imageCanvasY + imageCanvasHeight / 2, type: 'w' } // Левый средний
          ];

          handles.forEach(handle => {
            ctx.fillRect(handle.x - handleSize / 2, handle.y - handleSize / 2, handleSize, handleSize);
          });
        }

        ctx.restore();
      }

      // Добавляем текст с информацией
      ctx.fillStyle = '#333';
      ctx.font = '14px Arial';
      ctx.fillText(`Линий: ${this.geometryLines.length} | Масштаб: ${this.canvasZoom.toFixed(2)}x`, 10, 20);
    },
    handleCanvasWheel(event) {
      const canvas = this.$refs.geometryCanvas;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      // Старый масштаб
      const oldZoom = this.canvasZoom;

      // Изменяем масштаб
      const zoomDelta = event.deltaY > 0 ? 0.9 : 1.1;
      this.canvasZoom = Math.max(0.1, Math.min(10, this.canvasZoom * zoomDelta));

      // Корректируем смещение чтобы зум был относительно курсора
      const zoomRatio = this.canvasZoom / oldZoom;
      this.canvasOffsetX = mouseX - (mouseX - this.canvasOffsetX) * zoomRatio;
      this.canvasOffsetY = mouseY - (mouseY - this.canvasOffsetY) * zoomRatio;

      this.drawGeometry();
    },
    getResizeHandle(mouseX, mouseY, canvas) {
      if (!this.calibrationMode || !this.floorImage || !this.geometryBounds) return null;

      const scale = this.geometryBounds.scale;
      const transformX = (x) => this.geometryBounds.offsetX + (x - this.geometryBounds.minX) * scale;
      const transformY = (y) => this.geometryBounds.offsetY + (this.geometryBounds.maxY - y) * scale;

      const imageCanvasX = transformX(this.imageOffsetX);
      const imageCanvasY = transformY(this.imageOffsetY + this.imageHeight);
      const imageCanvasWidth = this.imageWidth * scale;
      const imageCanvasHeight = this.imageHeight * scale;

      const handleSize = 8;
      const threshold = 10;

      const handles = [
        { x: imageCanvasX, y: imageCanvasY, type: 'nw' },
        { x: imageCanvasX + imageCanvasWidth / 2, y: imageCanvasY, type: 'n' },
        { x: imageCanvasX + imageCanvasWidth, y: imageCanvasY, type: 'ne' },
        { x: imageCanvasX + imageCanvasWidth, y: imageCanvasY + imageCanvasHeight / 2, type: 'e' },
        { x: imageCanvasX + imageCanvasWidth, y: imageCanvasY + imageCanvasHeight, type: 'se' },
        { x: imageCanvasX + imageCanvasWidth / 2, y: imageCanvasY + imageCanvasHeight, type: 's' },
        { x: imageCanvasX, y: imageCanvasY + imageCanvasHeight, type: 'sw' },
        { x: imageCanvasX, y: imageCanvasY + imageCanvasHeight / 2, type: 'w' }
      ];

      for (const handle of handles) {
        const dist = Math.sqrt(Math.pow(mouseX - handle.x, 2) + Math.pow(mouseY - handle.y, 2));
        if (dist < threshold) {
          return handle.type;
        }
      }

      return null;
    },
    handleCanvasMouseDown(event) {
      const canvas = event.target;
      const rect = canvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      if (this.calibrationMode && this.floorImage) {
        // Check if clicking on resize handle
        const handle = this.getResizeHandle(mouseX, mouseY, canvas);
        if (handle) {
          this.isResizingImage = true;
          this.resizeHandle = handle;
          this.resizeStartX = mouseX;
          this.resizeStartY = mouseY;
          this.resizeStartImageX = this.imageOffsetX;
          this.resizeStartImageY = this.imageOffsetY;
          this.resizeStartImageWidth = this.imageWidth;
          this.resizeStartImageHeight = this.imageHeight;
          event.target.style.cursor = this.getResizeCursor(handle);
          return;
        }

        // Check if clicking on image
        const scale = this.geometryBounds.scale;
        const transformX = (x) => this.geometryBounds.offsetX + (x - this.geometryBounds.minX) * scale;
        const transformY = (y) => this.geometryBounds.offsetY + (this.geometryBounds.maxY - y) * scale;

        const imageCanvasX = transformX(this.imageOffsetX);
        const imageCanvasY = transformY(this.imageOffsetY + this.imageHeight);
        const imageCanvasWidth = this.imageWidth * scale;
        const imageCanvasHeight = this.imageHeight * scale;

        if (mouseX >= imageCanvasX && mouseX <= imageCanvasX + imageCanvasWidth &&
            mouseY >= imageCanvasY && mouseY <= imageCanvasY + imageCanvasHeight) {
          this.isDraggingImage = true;
          this.dragStartX = mouseX;
          this.dragStartY = mouseY;
          event.target.style.cursor = 'move';
          return;
        }
      }

      // Regular canvas dragging
      this.isDragging = true;
      this.dragStartX = event.clientX - this.canvasOffsetX;
      this.dragStartY = event.clientY - this.canvasOffsetY;
      event.target.style.cursor = 'grabbing';
    },
    getResizeCursor(handle) {
      const cursors = {
        'nw': 'nw-resize',
        'n': 'n-resize',
        'ne': 'ne-resize',
        'e': 'e-resize',
        'se': 'se-resize',
        's': 's-resize',
        'sw': 'sw-resize',
        'w': 'w-resize'
      };
      return cursors[handle] || 'default';
    },
    handleCanvasMouseMove(event) {
      const canvas = event.target;
      const rect = canvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      if (this.isResizingImage && this.calibrationMode) {
        const deltaX = mouseX - this.resizeStartX;
        const deltaY = mouseY - this.resizeStartY;
        const scale = this.geometryBounds.scale;
        const geomDeltaX = deltaX / scale;
        const geomDeltaY = -deltaY / scale; // Инвертируем для геометрических координат

        switch (this.resizeHandle) {
          case 'se': // Нижний правый угол - тянем вниз и вправо
            this.imageWidth = Math.max(10, this.resizeStartImageWidth + geomDeltaX);
            const newHeightSE = Math.max(10, this.resizeStartImageHeight - geomDeltaY);
            const deltaHeightSE = newHeightSE - this.resizeStartImageHeight;
            this.imageHeight = newHeightSE;
            this.imageOffsetY = this.resizeStartImageY - deltaHeightSE;
            break;
          case 'sw': // Нижний левый угол - тянем вниз и влево
            const newWidthSW = Math.max(10, this.resizeStartImageWidth - geomDeltaX);
            const deltaWidthSW = newWidthSW - this.resizeStartImageWidth;
            this.imageWidth = newWidthSW;
            this.imageOffsetX = this.resizeStartImageX - deltaWidthSW;
            const newHeightSW = Math.max(10, this.resizeStartImageHeight - geomDeltaY);
            const deltaHeightSW = newHeightSW - this.resizeStartImageHeight;
            this.imageHeight = newHeightSW;
            this.imageOffsetY = this.resizeStartImageY - deltaHeightSW;
            break;
          case 'ne': // Верхний правый угол - тянем вверх и вправо
            this.imageWidth = Math.max(10, this.resizeStartImageWidth + geomDeltaX);
            this.imageHeight = Math.max(10, this.resizeStartImageHeight + geomDeltaY);
            break;
          case 'nw': // Верхний левый угол - тянем вверх и влево
            const newWidthNW = Math.max(10, this.resizeStartImageWidth - geomDeltaX);
            const deltaWidthNW = newWidthNW - this.resizeStartImageWidth;
            this.imageWidth = newWidthNW;
            this.imageOffsetX = this.resizeStartImageX - deltaWidthNW;
            this.imageHeight = Math.max(10, this.resizeStartImageHeight + geomDeltaY);
            break;
          case 'e': // Правая сторона - тянем вправо
            this.imageWidth = Math.max(10, this.resizeStartImageWidth + geomDeltaX);
            break;
          case 'w': // Левая сторона - тянем влево
            const newWidthW = Math.max(10, this.resizeStartImageWidth - geomDeltaX);
            const deltaWidthW = newWidthW - this.resizeStartImageWidth;
            this.imageWidth = newWidthW;
            this.imageOffsetX = this.resizeStartImageX - deltaWidthW;
            break;
          case 's': // Нижняя сторона - тянем вниз
            const newHeightS = Math.max(10, this.resizeStartImageHeight - geomDeltaY);
            const deltaHeightS = newHeightS - this.resizeStartImageHeight;
            this.imageHeight = newHeightS;
            this.imageOffsetY = this.resizeStartImageY - deltaHeightS;
            break;
          case 'n': // Верхняя сторона - тянем вверх
            this.imageHeight = Math.max(10, this.resizeStartImageHeight + geomDeltaY);
            break;
        }

        this.drawGeometry();
      } else if (this.isDraggingImage && this.calibrationMode) {
        const deltaX = mouseX - this.dragStartX;
        const deltaY = mouseY - this.dragStartY;
        const geomDeltaX = deltaX / this.geometryBounds.scale;
        const geomDeltaY = -deltaY / this.geometryBounds.scale;

        this.imageOffsetX += geomDeltaX;
        this.imageOffsetY += geomDeltaY;

        this.dragStartX = mouseX;
        this.dragStartY = mouseY;

        this.drawGeometry();
      } else if (this.isDragging) {
        this.canvasOffsetX = event.clientX - this.dragStartX;
        this.canvasOffsetY = event.clientY - this.dragStartY;
        this.drawGeometry();
      } else if (this.calibrationMode && this.floorImage) {
        // Обновляем курсор при наведении на ручки
        const handle = this.getResizeHandle(mouseX, mouseY, canvas);
        if (handle) {
          canvas.style.cursor = this.getResizeCursor(handle);
        } else {
          const scale = this.geometryBounds.scale;
          const transformX = (x) => this.geometryBounds.offsetX + (x - this.geometryBounds.minX) * scale;
          const transformY = (y) => this.geometryBounds.offsetY + (this.geometryBounds.maxY - y) * scale;

          const imageCanvasX = transformX(this.imageOffsetX);
          const imageCanvasY = transformY(this.imageOffsetY + this.imageHeight);
          const imageCanvasWidth = this.imageWidth * scale;
          const imageCanvasHeight = this.imageHeight * scale;

          if (mouseX >= imageCanvasX && mouseX <= imageCanvasX + imageCanvasWidth &&
              mouseY >= imageCanvasY && mouseY <= imageCanvasY + imageCanvasHeight) {
            canvas.style.cursor = 'move';
          } else {
            canvas.style.cursor = 'default';
          }
        }
      } else if (!this.calibrationMode) {
        // Проверяем наведение на линию только если не в режиме калибровки
        this.checkLineHover(event);
      }

      // Обновляем координаты если включен режим координат
      if (this.coordinatesMode && this.geometryBounds) {
        this.updateCoordinates(mouseX, mouseY);
      }
    },
    handleCanvasMouseUp(event) {
      this.isDragging = false;
      this.isDraggingImage = false;
      this.isResizingImage = false;
      this.resizeHandle = null;
      if (event.target) {
        event.target.style.cursor = this.calibrationMode ? 'default' : 'grab';
      }
    },
    handleCanvasMouseLeave() {
      this.isDragging = false;
      this.isDraggingImage = false;
      this.isResizingImage = false;
      this.resizeHandle = null;
      this.showTooltip = false;
      this.hoveredLine = null;
      const canvas = this.$refs.geometryCanvas;
      if (canvas) {
        canvas.style.cursor = this.calibrationMode ? 'default' : 'grab';
      }
    },
    checkLineHover(event) {
      const canvas = this.$refs.geometryCanvas;
      if (!canvas || !this.geometryBounds) return;

      const rect = canvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      const { minX, maxX, minY, maxY } = this.geometryBounds;
      const dataWidth = maxX - minX;
      const dataHeight = maxY - minY;

      const padding = 50;
      const availableWidth = canvas.width - 2 * padding;
      const availableHeight = canvas.height - 2 * padding;

      let scaleX = availableWidth / dataWidth;
      let scaleY = availableHeight / dataHeight;
      let baseScale = Math.min(scaleX, scaleY);
      const scale = baseScale * this.canvasZoom;

      const scaledWidth = dataWidth * scale;
      const scaledHeight = dataHeight * scale;
      const offsetX = (canvas.width - scaledWidth) / 2 + this.canvasOffsetX;
      const offsetY = (canvas.height - scaledHeight) / 2 + this.canvasOffsetY;

      const transformX = (x) => offsetX + (x - minX) * scale;
      const transformY = (y) => offsetY + (maxY - y) * scale;

      // Проверяем близость к линиям
      const threshold = 10; // Расстояние в пикселях
      let foundLine = null;

      for (const geom of this.geometryLines) {
        for (let i = 0; i < geom.points.length - 1; i++) {
          const p1 = geom.points[i];
          const p2 = geom.points[i + 1];

          const x1 = transformX(p1.x);
          const y1 = transformY(p1.y);
          const x2 = transformX(p2.x);
          const y2 = transformY(p2.y);

          const dist = this.distanceToSegment(mouseX, mouseY, x1, y1, x2, y2);

          if (dist < threshold) {
            foundLine = geom;
            break;
          }
        }
        if (foundLine) break;
      }

      if (foundLine) {
        this.hoveredLine = foundLine;
        this.tooltipX = mouseX + 15;
        this.tooltipY = mouseY + 15;
        this.showTooltip = true;
      } else {
        this.showTooltip = false;
        this.hoveredLine = null;
      }
    },
    distanceToSegment(px, py, x1, y1, x2, y2) {
      const dx = x2 - x1;
      const dy = y2 - y1;
      const lengthSquared = dx * dx + dy * dy;

      if (lengthSquared === 0) {
        // Точка
        return Math.sqrt((px - x1) * (px - x1) + (py - y1) * (py - y1));
      }

      let t = ((px - x1) * dx + (py - y1) * dy) / lengthSquared;
      t = Math.max(0, Math.min(1, t));

      const projX = x1 + t * dx;
      const projY = y1 + t * dy;

      return Math.sqrt((px - projX) * (px - projX) + (py - projY) * (py - projY));
    },
    zoomIn() {
      this.canvasZoom = Math.min(10, this.canvasZoom * 1.2);
      this.drawGeometry();
    },
    zoomOut() {
      this.canvasZoom = Math.max(0.1, this.canvasZoom / 1.2);
      this.drawGeometry();
    },
    resetZoom() {
      this.canvasZoom = 1;
      this.canvasOffsetX = 0;
      this.canvasOffsetY = 0;
      this.drawGeometry();
    },
    enterCalibrationMode() {
      this.calibrationMode = true;
      const canvas = this.$refs.geometryCanvas;
      if (canvas) {
        canvas.style.cursor = 'default';
      }
    },
    cancelCalibration() {
      this.calibrationMode = false;
      this.drawGeometry();
      const canvas = this.$refs.geometryCanvas;
      if (canvas) {
        canvas.style.cursor = 'grab';
      }
    },
    async saveCalibration() {
      if (!this.currentFloorSchemeId) {
        this.showErrorSnackbar("Ошибка: ID схемы этажа не найден");
        return;
      }

      try {
        await saveBimProjectFloorSchemeCalibration({
          bim_project_floor_scheme_id: this.currentFloorSchemeId,
          image_offset_x: this.imageOffsetX,
          image_offset_y: this.imageOffsetY,
          image_width: this.imageWidth,
          image_height: this.imageHeight
        });

        this.showSucceedSnackbar("Калибровка успешно сохранена");
        this.calibrationMode = false;

        // Обновить данные схем этажей
        if (this.selectedProject && this.selectedProject.bim_project_id) {
          await this.loadFloorSchemesSilently(this.selectedProject.bim_project_id);
        }

        const canvas = this.$refs.geometryCanvas;
        if (canvas) {
          canvas.style.cursor = 'grab';
        }
      } catch (error) {
        console.error("Error saving calibration:", error);
        this.showErrorSnackbar("Ошибка при сохранении калибровки");
      }
    },
    getColorForIndex(index) {
      const colors = [
        '#1976d2', '#388e3c', '#d32f2f', '#f57c00',
        '#7b1fa2', '#0097a7', '#c2185b', '#5d4037'
      ];
      return colors[index % colors.length];
    },
    toggleType(bimTypeId) {
      const index = this.selectedTypes.indexOf(bimTypeId);
      if (index > -1) {
        this.selectedTypes.splice(index, 1);
      } else {
        this.selectedTypes.push(bimTypeId);
      }
      this.allTypesSelected = this.selectedTypes.length === Object.keys(this.typeSummary).length;
      this.drawGeometry();
    },
    toggleAllTypes() {
      if (this.allTypesSelected) {
        this.selectedTypes = [];
        this.allTypesSelected = false;
      } else {
        this.selectedTypes = Object.keys(this.typeSummary);
        this.allTypesSelected = true;
      }
      this.drawGeometry();
    },
    formatVolume(volume) {
      return volume.toFixed(3);
    },
    updateCoordinates(canvasX, canvasY) {
      if (!this.geometryBounds) return;

      const { minX, maxX, minY, maxY, scale, offsetX, offsetY } = this.geometryBounds;

      // Обратное преобразование (offsetX/Y и scale уже содержат zoom и pan)
      const geomX = minX + (canvasX - offsetX) / scale;
      const geomY = maxY - (canvasY - offsetY) / scale;

      this.currentCoordinates = {
        x: geomX,
        y: geomY
      };
    },
    getLevelLabel(value) {
      const level = this.availableLevelsForSelect.find(l => l.value === value);
      return level ? level.label : value;
    },
    getTypeLabel(value) {
      const type = this.availableTypes.find(t => t.bim_type_id === value);
      return type ? type.type_name : value;
    },
    getMarkLabel(value) {
      const opt = this.availableMarksForSelect.find(o => o.value === value);
      return opt ? opt.label : (value === '' ? '(Пусто)' : value);
    },
    getLineSlotLabel(value) {
      const opt = this.lineFilterSelectOptions.find((o) => o.value === value);
      return opt ? opt.label : value;
    },
    lineFilterItemMatches(item, raw) {
      if (raw === '1' || raw === '2' || raw === '3') {
        if (raw === '1') return !!(item.floor_param_type_id || item.floor_param_type_name);
        if (raw === '2') return !!(item.floor_param_type_id2 || item.floor_param_type_name2);
        return !!(item.floor_param_type_id3 || item.floor_param_type_name3);
      }
      let spec;
      try {
        spec = JSON.parse(raw);
      } catch {
        return false;
      }
      if (spec.fb) {
        if (spec.s === 1) return !!(item.floor_param_type_id || item.floor_param_type_name);
        if (spec.s === 2) return !!(item.floor_param_type_id2 || item.floor_param_type_name2);
        if (spec.s === 3) return !!(item.floor_param_type_id3 || item.floor_param_type_name3);
        return false;
      }
      const s = spec.s;
      const i = spec.i;
      const n = spec.n;
      if (s === 1) {
        if (i != null) return Number(item.floor_param_type_id) === i;
        return n != null && String(item.floor_param_type_name || '').trim() === n;
      }
      if (s === 2) {
        if (i != null) return Number(item.floor_param_type_id2) === i;
        return n != null && String(item.floor_param_type_name2 || '').trim() === n;
      }
      if (s === 3) {
        if (i != null) return Number(item.floor_param_type_id3) === i;
        return n != null && String(item.floor_param_type_name3 || '').trim() === n;
      }
      return false;
    },
    async exportItemsToExcel() {
      if (!this.selectedProject) return;

      try {
        const API_URL = process.env.VUE_APP_API_URL || 'http://localhost:8000';
        let url = `${API_URL}/handbook/bim_projects/${this.selectedProject.bim_project_id}/items/export-excel/`;

        // Добавляем параметр категории если выбрана
        if (this.selectedCategoryId) {
          url += `?bim_category_id=${this.selectedCategoryId}`;
        }

        // Получаем токен из cookies через cookiesService
        const token = cookiesServiceInstance.getAccessToken();

        if (!token) {
          this.showErrorSnackbar('Ошибка авторизации');
          return;
        }

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Ошибка при экспорте');
        }

        // Получаем blob из ответа
        const blob = await response.blob();

        // Создаем ссылку для скачивания
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `bim_project_${this.selectedProject.bim_project_id}_positions.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        this.showSucceedSnackbar('Excel файл успешно экспортирован');
      } catch (error) {
        console.error('Ошибка экспорта:', error);
        this.showErrorSnackbar('Ошибка при экспорте в Excel');
      }
    }
  },
  created() {
    // Сначала восстанавливаем состояние фильтров из URL
    this.initializeFromURL();
  },
  async mounted() {
    // Загружаем справочники и проекты (учитывая фильтры из URL).
    // Категории теперь зависят от выбранного проекта и загружаются в selectProject.
    await Promise.all([
      this.loadProjectTypes(),
      this.loadProjects()
    ]);

    // После загрузки проектов можно выбрать проект из URL
    this.initializeSelectedProjectFromURL();
  },
  setup() {
    const { showSucceedSnackbar, showErrorSnackbar, showWarningSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar, showErrorSnackbar, showWarningSnackbar };
  }
};
</script>

<style scoped>
.bim-projects {
  padding: 8px;
  height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.bim-projects__header {
  background: white;
  border: 1px solid #ddd;
  padding: 6px 8px;
  box-shadow: none;
  flex-shrink: 0;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-label {
  font-size: 12px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
}

.simple-select {
  min-width: 250px;
  padding: 4px 8px;
  font-size: 12px;
  border: 1px solid #ccc;
  border-radius: 3px;
  background: white;
  cursor: pointer;
}

.simple-select:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.bim-projects__content {
  display: flex;
  gap: 6px;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.bim-projects__column {
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid #ddd;
  overflow: hidden;
}

.projects-column {
  flex: 0 0 350px;
}

.details-column {
  flex: 1;
  min-width: 0;
}

.column-header {
  background: #f0f0f0;
  padding: 4px 8px;
  color: #333;
  font-weight: 600;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  border-bottom: 1px solid #ddd;
}

.column-title {
  flex: 1;
}

.search-container {
  padding: 6px 8px;
  background: #ffffff;
  border-bottom: 1px solid #e0e0e0;
}

.search-input {
  font-size: 12px;
}

.search-input :deep(.q-field__control) {
  height: 28px;
  min-height: 28px;
}

.search-input :deep(.q-field__marginal) {
  height: 28px;
}

.search-input :deep(input) {
  font-size: 12px;
  padding: 2px 4px;
}

.column-list {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.column-list.tree-list {
  padding: 4px 0;
}

.tree-node {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 14px;
  color: #555;
  transition: background 0.1s;
}

.tree-node:hover {
  background: #e8f4fd;
}

.tree-node__icon {
  flex-shrink: 0;
  margin-right: 6px;
  color: #888;
}

.tree-node__label {
  font-weight: 500;
}

.tree-node--resident {
  padding-left: 10px;
  font-weight: 600;
  color: #333;
}

.tree-node--type {
  padding-left: 24px;
  font-size: 13px;
}

.tree-node--block {
  padding-left: 40px;
  font-size: 13px;
  color: #666;
}

.tree-leaf.list-item {
  padding-left: 56px !important;
}

.list-item {
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.1s;
  min-height: 36px;
  padding: 4px 8px !important;
}

.list-item :deep(.q-item__section) {
  padding: 0 6px;
}

.list-item:hover {
  background: #e8f4fd;
}

.list-item.q-item--active {
  background: #cce5ff;
  border-left: 2px solid #1976d2;
}

.item-label {
  font-weight: 500;
  color: #333;
  font-size: 13px;
  line-height: 1.35;
  margin-bottom: 2px;
}

.item-label-id {
  font-weight: 400;
  color: #666;
  font-size: 12px;
  margin-left: 4px;
}

.item-type {
  font-size: 11px;
  color: #666;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.item-type .type-text {
  color: #1976d2;
  font-weight: 500;
}

.item-type .separator {
  color: #ccc;
}

.item-type .date-text {
  color: #999;
}

.item-caption {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

.action-buttons {
  width: auto !important;
  min-width: 100px;
}

.action-buttons .action-btn-more.wrapper {
  padding: 4px 10px;
  min-height: 28px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  border-radius: 4px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  gap: 6px;
  padding: 16px;
}

.empty-state p {
  margin: 0;
  font-size: 11px;
}

.empty-state .q-icon {
  font-size: 28px !important;
}

.empty-state-small {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  color: #999;
  gap: 8px;
}

.empty-state-small p {
  margin: 0;
  font-size: 12px;
}

.empty-state-small .q-icon {
  font-size: 32px !important;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 80px;
}

.loading-container-small {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.tabs-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.tab-panels {
  flex: 1;
  overflow: hidden;
}

.tab-panel {
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.tab-header {
  padding: 10px 14px;
  background: #f5f5f5;
  border-bottom: 1px solid #ddd;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.tab-title {
  font-weight: 600;
  font-size: 14px;
  color: #333;
  white-space: nowrap;
}

.tab-categories {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.tab-categories__label {
  font-size: 14px;
  font-weight: 500;
  color: #444;
  flex-shrink: 0;
}

.tab-categories__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tab-categories__chips .q-chip {
  margin: 0;
  font-size: 14px !important;
  min-height: 28px;
  padding: 6px 12px;
}

.tab-categories--top {
  width: 100%;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
  margin-bottom: 8px;
}

.tab-header-row {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 12px;
  min-height: 40px;
}

.tab-header-row--items-filters {
  align-items: flex-start;
}

.tab-header-row .tab-title {
  flex-shrink: 0;
}

.tab-filters-stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.tab-filters-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  width: 100%;
}

.tab-filters-row--second {
  align-items: center;
}

.filters__item--select {
  width: 200px;
  flex-shrink: 0;
}

.filters__item--types {
  min-width: 220px;
  width: min(280px, 100%);
}

.filters__item--search {
  flex: 1;
  min-width: 200px;
  max-width: 360px;
}

.filters__toggles {
  display: flex;
  flex-direction: column;
  gap: 2px;
  justify-content: center;
}

.filters__toggle-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tab-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.table-container {
  flex: 1;
  overflow: auto;
}

.data-table {
  font-size: 12px;
}

.data-table :deep(.q-table thead tr th) {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f0f4f8;
  font-weight: 600;
  color: #333;
  font-size: 12px;
  padding: 8px 10px;
  border-bottom: 1px solid #ddd;
}

.data-table :deep(.q-table tbody tr) {
  font-size: 12px;
  height: 34px;
}

.data-table :deep(.q-table tbody td) {
  padding: 6px 10px;
  border-bottom: 1px solid #eee;
}

.data-table :deep(.q-table tbody tr:hover) {
  background: #e8f4fd;
}

/* Колонка «Тип»: читаема при любом масштабе, не сжимается в узкий столбик */
.bim-type-cell {
  min-width: 220px;
  max-width: min(400px, 40vw);
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  vertical-align: top;
}
.bim-type-cell__content {
  word-break: break-word;
  overflow-wrap: break-word;
  line-height: 1.35;
}

.bim-mark-cell {
  min-width: 100px;
  max-width: 200px;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.bim-room-cell {
  min-width: 120px;
  max-width: min(320px, 30vw);
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.35;
}

.totals-row {
  background: #f5f5f5;
  font-weight: 600;
}

.totals-row td {
  border-top: 2px solid #ddd !important;
}

.column-header .q-btn {
  padding: 2px;
  min-width: 24px;
  min-height: 24px;
}

.column-header .q-icon {
  font-size: 14px;
}

.column-header .q-badge {
  font-size: 10px;
  padding: 2px 4px;
}

.header-checkbox {
  margin-right: 4px;
}

.header-checkbox :deep(.q-checkbox__inner) {
  font-size: 16px;
  width: 16px;
  height: 16px;
}

/* Стили модального окна */
.bim-modal {
  min-width: 500px;
  max-width: 700px;
}

.file-input {
  font-size: 12px;
}

.file-input :deep(.q-field__control) {
  min-height: 40px;
}

.file-info {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  font-size: 12px;
}

.file-info-item {
  margin-bottom: 4px;
}

.file-info-item:last-child {
  margin-bottom: 0;
}

.warning-message {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.5;
  color: #856404;

  strong {
    font-weight: 600;
  }

  .q-icon {
    flex-shrink: 0;
    margin-top: 2px;
  }
}

.project-categories-block {
  border: 2px solid #f44336;
  border-radius: 8px;
  background-color: #ffebee;
  padding: 12px;
}

.project-categories-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f44336;
}

.project-categories-list {
  padding: 8px;
  background-color: #ffffff;
  border-radius: 6px;
  border: 1px solid #ffcdd2;
  max-height: 200px;
  overflow-y: auto;
}

.project-category-item {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  align-items: center;
  padding: 8px 12px;
  margin-bottom: 4px;
  background-color: #ffffff;
  border-radius: 4px;
  border-left: 3px solid #f44336;
  font-size: 13px;
  color: #333;
  transition: background-color 0.2s;
}

.project-category-item:hover {
  background-color: #ffebee;
}

.project-category-item:last-child {
  margin-bottom: 0;
}

.category-name {
  font-weight: 500;
}

.category-details {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.category-date {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
}

.category-employee {
  font-size: 11px;
  color: #1976d2;
  font-weight: 500;
  white-space: nowrap;
}

.customInput__label {
  font-size: 12px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
}

.floor-scheme-preview {
  max-width: 100px;
  max-height: 60px;
  cursor: pointer;
  border: 1px solid #ddd;
  border-radius: 4px;
  transition: transform 0.2s;
}

.floor-scheme-preview:hover {
  transform: scale(1.05);
  border-color: #1976d2;
}

.image-link {
  color: #1976d2;
  text-decoration: none;
  font-size: 11px;
}

.image-link:hover {
  text-decoration: underline;
}

.import-json-info {
  padding: 8px 0;
}

.import-json-info p {
  line-height: 1.5;
}

.bim-modal__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.bim-modal__title {
  font-size: 20px;
  font-weight: 600;
  color: #373D48;
  line-height: 20px;
}

.bim-modal__close {
  color: #9CA3AF;
}

.bim-modal__content {
  padding: 20px 24px;
}

.bim-form {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 15px;
}

.bim-form__section {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.bim-form__item {
  width: 100%;
}

.param-value-rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.param-value-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px;
}

.param-value-row__select {
  flex: 1;
  min-width: 180px;
}

.param-value-row__input {
  flex: 1;
  min-width: 140px;
}

.param-value-row__actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.param-value-add-row {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.copy-floors-inputs {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.copy-floors-input-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.copy-floors-input-cell {
  flex: 1;
  min-width: 0;
}

.copy-floors-add-row {
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.bim-modal__footer {
  width: 100%;
  height: 46px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-top: 15px;
}

@media (max-width: 600px) {
  .bim-modal {
    min-width: 100%;
  }
}

.geometry-section {
  height: calc(100vh - 150px);
  padding: 16px !important;
}

.canvas-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  position: relative;
}

.geometry-canvas {
  display: block;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  cursor: grab;
}

.geometry-canvas:active {
  cursor: grabbing;
}

.geometry-layout {
  display: flex;
  height: 100%;
  gap: 16px;
}

.canvas-container {
  flex: 1;
  position: relative;
  min-width: 0;
}

.types-panel {
  width: 340px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
}

.types-header {
  padding: 16px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}

.types-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.type-item {
  padding: 8px;
  border-radius: 6px;
  transition: background-color 0.2s;
}

.type-item:hover {
  background-color: #f5f5f5;
}

.type-item-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-left: 8px;
}

.type-name {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  line-height: 1.4;
}

.type-stats {
  display: flex;
  gap: 12px;
  font-size: 11px;
  color: #666;
}

.type-volume {
  font-weight: 600;
  color: #1976d2;
}

.type-count {
  color: #757575;
}

.canvas-controls {
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  gap: 4px;
  background: rgba(255, 255, 255, 0.9);
  padding: 8px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.coordinates-display {
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(255, 255, 255, 0.95);
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 180px;
}

.coordinates-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e0e0e0;
}

.coordinates-values {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.coordinate-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
}

.coordinate-label {
  font-weight: 500;
  color: #666;
  margin-right: 12px;
}

.coordinate-value {
  font-weight: 600;
  color: #1976d2;
  font-family: 'Courier New', monospace;
}

.geometry-tooltip {
  position: absolute;
  background: rgba(0, 0, 0, 0.85);
  color: white;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 12px;
  pointer-events: none;
  z-index: 1000;
  min-width: 200px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.tooltip-title {
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 6px;
  color: #4fc3f7;
}

.tooltip-item {
  margin: 4px 0;
  line-height: 1.4;
}

.tooltip-item strong {
  color: #90caf9;
}

.opacity-control {
  position: absolute;
  bottom: 10px;
  left: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.95);
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 12px;
}

.opacity-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
}

.opacity-slider {
  flex: 1;
  min-width: 150px;
}

.opacity-value {
  font-size: 12px;
  font-weight: 600;
  color: #1976d2;
  min-width: 40px;
  text-align: right;
}

/* Crop dialog styles */
.crop-section {
  height: calc(100vh - 150px);
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: hidden;
}

.crop-instructions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #e3f2fd;
  border-radius: 4px;
  font-size: 14px;
  color: #1976d2;
}

.crop-canvas-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  background: #f5f5f5;
  border-radius: 4px;
  padding: 20px;
}

.crop-canvas {
  cursor: crosshair;
  border: 1px solid #ddd;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  background: white;
}

/* Copy to floors dialog styles */
.copy-floors-list {
  max-height: 400px;
  overflow-y: auto;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fafafa;
}

.copy-floors-list .q-checkbox {
  width: 100%;
}
</style>
